/**
 * =============================================================================
 * IDS CRM Web - Check Form Settings (with Draft Editing)
 * =============================================================================
 *
 * PURPOSE:
 * Settings page for check form templates. Admins, managers and team leaders can:
 * - View active templates
 * - Create drafts from active templates
 * - Reopen a saved draft (a new template, a variant or a new version) to keep editing
 * - Edit draft templates (name, description, section titles, field properties)
 * - Save, discard and publish drafts
 * - Preview form appearance
 *
 * CONSTRAINTS (this phase):
 * - Fields and sections can be added and archived, but not deleted (a follow-up question can be removed)
 * - Cannot change field_key, field_type, system_field, form_type
 * - Live forms are not affected until a draft is published
 *
 * =============================================================================
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import CheckFormPreview from '../components/CheckFormPreview';
import FormDesigner from '../components/FormDesigner';
import { TYPE_LABELS } from '../utils/formSchemaOps';
import EquipmentTypeSettings from '../components/EquipmentTypeSettings';
import { formatDateTime } from '../utils/format';
import './CheckFormSettings.css';
import {
  CHECK_CATEGORIES,
  DEFAULT_WORK_LOG_TYPES,
  isScheduledMaintenanceTemplate,
  reactiveWorkLogTypeOptions,
  ASSET_TYPE_GROUPS,
  MAINTENANCE_VISIT_TYPES,
  SERVICE_YEARS,
  serviceYearsTargetingApplies,
  buildAssetTypeGroups,
  getCategoryLabel,
  getWorkLogTypeLabel,
  getAssetTypeLabel,
  getEquipmentTypeLabel
} from '../utils/checkFormTargeting';

const FORM_TYPE_LABELS = {
  daily_van_check: 'Daily Van Check',
  brush_robot_check: 'Brush Robot Check',
  spray_robot_check: 'Spray Robot Check'
};

const STATUS_LABELS = {
  draft: 'Draft',
  active: 'Active',
  superseded: 'Superseded',
  archived: 'Archived'
};


const formTypeLabel = (formType) => FORM_TYPE_LABELS[formType]
  || String(formType || '').replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());

/** The section and question keys a schema carries (follow-ups included). */
function keysOfSchema(schema) {
  const sections = new Set();
  const fields = new Set();
  (schema?.sections || []).forEach(section => {
    if (section.section_key) sections.add(section.section_key);
    (section.fields || []).forEach(field => {
      if (field.field_key) fields.add(field.field_key);
      if (field.follow_up?.field?.field_key) fields.add(field.follow_up.field.field_key);
    });
  });
  return { sections, fields };
}

function CheckFormSettings() {
  const { authFetch, user } = useAuth();
  // Mirrors the API's authorize(ROLES.OPS) on every template write — team
  // leaders were granted this tab to run check forms (Gareth 2026-08-03),
  // not just to read them.
  const canEdit = ['admin', 'manager', 'team_leader'].includes(user?.role);

  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // L11: coverage matrix - fetched on demand only (heavier endpoint)
  const [coverage, setCoverage] = useState(null);
  const [coverageLoading, setCoverageLoading] = useState(false);

  const fetchCoverage = useCallback(async () => {
    setCoverageLoading(true);
    try {
      const response = await authFetch('/api/v1/check-form-templates/coverage');
      if (response.ok) {
        setCoverage(await response.json());
      }
    } catch (err) {
      console.error('Failed to fetch coverage matrix:', err);
    } finally {
      setCoverageLoading(false);
    }
  }, [authFetch]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [detailView, setDetailView] = useState('fields');

  // Draft editing state
  const [isEditingDraft, setIsEditingDraft] = useState(false);
  const [draftTemplate, setDraftTemplate] = useState(null);
  const [draftSchema, setDraftSchema] = useState(null);
  const [draftName, setDraftName] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [existingDraftInfo, setExistingDraftInfo] = useState(null);
  const [draftsMap, setDraftsMap] = useState({});

  // Publishing state
  const [publishing, setPublishing] = useState(false);
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);
  const [publishErrors, setPublishErrors] = useState([]);
  const [targetingConflict, setTargetingConflict] = useState(null);

  // Targeting state for draft editing
  // draftUsage: 'reactive' (tickets and other jobs) or 'scheduled' (Log Work on
  // Quarterly Health Check / Annual Service jobs, and maintenance sign-off)
  const [draftUsage, setDraftUsage] = useState('reactive');
  const [draftCheckCategory, setDraftCheckCategory] = useState(null);
  const [draftWorkLogTypes, setDraftWorkLogTypes] = useState(null);
  const [draftAssetTypes, setDraftAssetTypes] = useState(null);
  const [draftMaintenanceTypes, setDraftMaintenanceTypes] = useState(null);
  const [draftServiceYears, setDraftServiceYears] = useState(null);
  const [draftSalesContexts, setDraftSalesContexts] = useState(null);
  const [draftEquipmentTypes, setDraftEquipmentTypes] = useState(null);
  const [workLogTypesMode, setWorkLogTypesMode] = useState('all');
  const [assetTypesMode, setAssetTypesMode] = useState('all');
  const [maintenanceTypesMode, setMaintenanceTypesMode] = useState('all');
  const [serviceYearsMode, setServiceYearsMode] = useState('all');
  const [equipmentTypesMode, setEquipmentTypesMode] = useState('all');

  // New variant draft state
  const [isNewVariant, setIsNewVariant] = useState(false);
  const [showVariantModal, setShowVariantModal] = useState(false);
  const [variantForm, setVariantForm] = useState({ name: '', description: '', error: '' });

  // Keys a published version of the draft's form has carried
  // ({ sections: Set, fields: Set }): those can only be archived. Anything
  // else in the draft can be deleted. Null when not known: nothing is deletable.
  const [draftPublishedKeys, setDraftPublishedKeys] = useState(null);

  // Settings view of a published form: archived items are left out unless asked for.
  const [showArchivedInView, setShowArchivedInView] = useState(false);

  // Create New Template modal state
  const [showCreateNewModal, setShowCreateNewModal] = useState(false);
  const [createNewLoading, setCreateNewLoading] = useState(false);
  const [createNewError, setCreateNewError] = useState('');
  const [newTemplateData, setNewTemplateData] = useState({
    name: '',
    description: '',
    check_category: 'generic_check',
    usage: 'reactive',
    workLogTypesMode: 'all',
    targetWorkLogTypes: [],
    assetTypesMode: 'all',
    targetAssetTypes: [],
    maintenanceTypesMode: 'all',
    targetMaintenanceTypes: [],
    serviceYearsMode: 'all',
    targetServiceYears: [],
    equipmentTypesMode: 'all',
    targetEquipmentTypes: [],
    targetSalesContexts: [],
    // Phase 10: sales forms target contexts OR pipeline stages, never both
    salesTargetMode: 'contexts',
    targetPipelineStages: [],
    includeInContractPack: false
  });

  // Sales-form contexts, loaded live from the config-driven endpoint so
  // managers can extend them without a code change.
  const [salesContextOptions, setSalesContextOptions] = useState([]);

  // Equipment types (E1) — config-driven list for equipment_check targeting.
  const [equipmentTypeOptions, setEquipmentTypeOptions] = useState([]);

  // Phase 10: product-keyed stage lists for the stage-trigger targeting UI
  const [pipelineStagesByType, setPipelineStagesByType] = useState({});
  const [stageTargetProduct, setStageTargetProduct] = useState('');

  const loadPipelineStages = useCallback(async () => {
    try {
      const response = await authFetch('/api/v1/opportunities/pipeline-stages');
      if (response.ok) {
        const data = await response.json();
        setPipelineStagesByType(data.data || {});
      }
    } catch (err) {
      console.error('Failed to load pipeline stages:', err);
    }
  }, [authFetch]);

  // Status filter state
  const [statusFilter, setStatusFilter] = useState('active');

  // Work log type targeting options - loaded live from Scheduled & Ticketed Support
  // config (/support/work-log-config) so custom work log types are targetable.
  // Falls back to DEFAULT_WORK_LOG_TYPES only if that request fails or is empty.
  const [workLogTypeOptions, setWorkLogTypeOptions] = useState(DEFAULT_WORK_LOG_TYPES);

  // Asset type targeting options - loaded live from the asset config so
  // admin-created asset types are targetable without a code change.
  // Falls back to the static ASSET_TYPE_GROUPS if the request fails.
  const [assetTypeGroups, setAssetTypeGroups] = useState(ASSET_TYPE_GROUPS);

  // Archive/Restore state
  const [archiving, setArchiving] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  // What archiving would leave with no form: { loading, uncovered, failed }.
  const [archiveImpact, setArchiveImpact] = useState(null);
  // The form's history, read when it is opened: { templateId, loading, rows, failed }.
  const [formHistory, setFormHistory] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [restoreConflict, setRestoreConflict] = useState(null);

  // Rename state
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [renameError, setRenameError] = useState('');

  const fetchTemplates = useCallback(async (filter = statusFilter) => {
    try {
      setLoading(true);
      setError(null);

      let url = '/api/v1/check-form-templates';
      if (filter !== 'all') {
        url += `?status=${filter}`;
      }

      const response = await authFetch(url);
      if (!response.ok) {
        throw new Error('Failed to load check form templates');
      }
      const data = await response.json();
      setTemplates(data.data || []);

      const draftsResponse = await authFetch('/api/v1/check-form-templates?status=draft');
      if (draftsResponse.ok) {
        const draftsData = await draftsResponse.json();
        const drafts = {};
        (draftsData.data || []).forEach(draft => {
          drafts[draft.template_key] = draft;
        });
        setDraftsMap(drafts);
      }
    } catch (err) {
      setError(err.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, [authFetch, statusFilter]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  useEffect(() => {
    const fetchWorkLogTypeOptions = async () => {
      try {
        const response = await authFetch('/api/v1/support/work-log-config');
        if (!response.ok) {
          throw new Error('Failed to load work log type config');
        }
        const data = await response.json();
        const types = (data.work_types || [])
          .filter(t => t.value)
          .map(t => ({ value: t.value, label: t.label || t.value }));
        // Only fall back to defaults if the live config is unavailable or empty -
        // an empty list here is not expected to be a valid steady state.
        // Reactive work types only: scheduled maintenance is chosen via the
        // template Usage toggle, never as a work log type.
        setWorkLogTypeOptions(reactiveWorkLogTypeOptions(types.length > 0 ? types : DEFAULT_WORK_LOG_TYPES));
      } catch (err) {
        console.error('[Check Form Settings] Failed to load work log type config, using fallback defaults:', err);
        setWorkLogTypeOptions(reactiveWorkLogTypeOptions(DEFAULT_WORK_LOG_TYPES));
      }
    };

    fetchWorkLogTypeOptions();
  }, [authFetch]);

  useEffect(() => {
    const fetchAssetTypeOptions = async () => {
      try {
        const response = await authFetch('/api/v1/assets/config');
        if (!response.ok) {
          throw new Error('Failed to load asset config');
        }
        const data = await response.json();
        setAssetTypeGroups(buildAssetTypeGroups(data.data?.asset_types));
      } catch (err) {
        console.error('[Check Form Settings] Failed to load asset types from config, using fallback defaults:', err);
        setAssetTypeGroups(ASSET_TYPE_GROUPS);
      }
    };

    fetchAssetTypeOptions();
  }, [authFetch]);

  // Loaded on-demand when the create-new modal opens (not on mount) so the
  // ordered mock chains in CheckFormSettings.test.js are unaffected.
  // Activity-type forms (Gareth 2026-08-04): the targeting vocabulary IS the
  // configurable Commercial activity types list — one list drives Log
  // Activity's type picker AND form targeting. The endpoint returns active
  // items only; label lookups for historic targets fall back to prettified
  // values in getSalesContextLabel.
  const loadSalesContexts = useCallback(async () => {
    try {
      const response = await authFetch('/api/v1/support/config-lists');
      if (response.ok) {
        const body = await response.json();
        setSalesContextOptions(body.data?.commercial_activity_types || []);
      }
    } catch (err) {
      console.error('[Check Form Settings] Failed to load activity types:', err);
    }
  }, [authFetch]);

  // Same on-demand pattern as sales contexts (keeps the ordered test mocks
  // unaffected): loaded when a targeting editor that needs it opens.
  const loadEquipmentTypes = useCallback(async () => {
    try {
      const response = await authFetch('/api/v1/check-form-templates/equipment-types');
      if (response.ok) {
        const data = await response.json();
        setEquipmentTypeOptions(data.data || []);
      }
    } catch (err) {
      console.error('[Check Form Settings] Failed to load equipment types:', err);
    }
  }, [authFetch]);

  // Build the checkbox option list for a targeting picker: the live/fallback options,
  // plus any already-selected values that no longer appear in that list (e.g. a work
  // log type that was renamed or deactivated since the template was targeted). This
  // keeps stale saved selections visible and checked instead of silently vanishing.
  const getWorkLogTypeCheckboxOptions = useCallback((selectedValues) => {
    const known = new Set(workLogTypeOptions.map(t => t.value));
    const staleOptions = (selectedValues || [])
      .filter(v => v && !known.has(v))
      .map(v => ({ value: v, label: `${getWorkLogTypeLabel(v, workLogTypeOptions)} (no longer configured)` }));
    return [...workLogTypeOptions, ...staleOptions];
  }, [workLogTypeOptions]);

  // Sales-context label from the dynamic list; falls back to a prettified
  // code when the list hasn't loaded or the value was removed from it.
  const getSalesContextLabel = useCallback((value) => (
    salesContextOptions.find(ctx => ctx.value === value)?.label
      || String(value).replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase())
  ), [salesContextOptions]);

  // Same shape as getWorkLogTypeCheckboxOptions: active categories plus any
  // already-targeted values that are deactivated/removed, kept visible so
  // saved targeting never silently vanishes.
  const getSalesContextCheckboxOptions = useCallback((selectedValues) => {
    const activeOptions = salesContextOptions.filter(ctx => ctx.active !== false);
    const known = new Set(activeOptions.map(ctx => ctx.value));
    const staleOptions = (selectedValues || [])
      .filter(v => v && !known.has(v))
      .map(v => ({ value: v, label: `${getSalesContextLabel(v)} (no longer configured)` }));
    return [...activeOptions, ...staleOptions];
  }, [salesContextOptions, getSalesContextLabel]);

  const handleSelectTemplate = async (template) => {
    if (isEditingDraft) {
      return;
    }
    try {
      setDetailLoading(true);
      setDetailError(null);
      setExistingDraftInfo(null);
      const response = await authFetch(`/api/v1/check-form-templates/${template.template_id}`);
      if (!response.ok) {
        throw new Error('Failed to load template details');
      }
      const data = await response.json();
      setSelectedTemplate(data);
      // Sales-form targeting is shown with the dynamic list's labels.
      if (data.check_category === 'sales_form' && salesContextOptions.length === 0) {
        loadSalesContexts();
      }
    } catch (err) {
      setDetailError(err.message || 'Failed to load template');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleCreateDraft = async () => {
    if (!selectedTemplate || !canEdit) return;

    try {
      setDetailLoading(true);
      setDetailError(null);
      setSaveMessage(null);
      setExistingDraftInfo(null);

      const response = await authFetch(`/api/v1/check-form-templates/${selectedTemplate.template_id}/create-draft`, {
        method: 'POST'
      });

      if (response.status === 409) {
        const data = await response.json();
        setExistingDraftInfo({
          message: data.message,
          draftId: data.existing_draft_id
        });
        setDetailLoading(false);
        return;
      }

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to create draft');
      }

      const draft = await response.json();
      // A new version: the active one holds every key ever published, since
      // a published key can only be archived, never deleted.
      enterDraftEditor(draft, false, selectedTemplate.schema_json || null);
    } catch (err) {
      setDetailError(err.message || 'Failed to create draft');
    } finally {
      setDetailLoading(false);
    }
  };

  // A variant is a new form, so it is named and described before it is made
  // (Gareth 2026-10-21): a copy left as "Copy of ..." carried its source's
  // wording onto the live form.
  const openVariantModal = () => {
    if (!selectedTemplate || !canEdit) return;
    setVariantForm({
      name: `Copy of ${selectedTemplate.name}`,
      description: selectedTemplate.schema_json?.description || '',
      error: ''
    });
    setShowVariantModal(true);
  };

  const closeVariantModal = () => {
    setShowVariantModal(false);
  };

  const handleCreateVariantDraft = async () => {
    if (!selectedTemplate || !canEdit) return;

    const name = variantForm.name.trim();
    if (!name) {
      setVariantForm(prev => ({ ...prev, error: 'Give the new form a name.' }));
      return;
    }

    try {
      setDetailLoading(true);
      setDetailError(null);
      setSaveMessage(null);

      const response = await authFetch(`/api/v1/check-form-templates/${selectedTemplate.template_id}/create-variant-draft`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, description: variantForm.description.trim() })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to create variant draft');
      }

      const draft = await response.json();
      setShowVariantModal(false);
      // A new form: nothing in it has been published yet.
      enterDraftEditor(draft, true, null);
    } catch (err) {
      setVariantForm(prev => ({ ...prev, error: err.message || 'Failed to create variant draft' }));
    } finally {
      setDetailLoading(false);
    }
  };

  const openCreateNewModal = () => {
    setNewTemplateData({
      name: '',
      description: '',
      check_category: 'generic_check',
      usage: 'reactive',
      workLogTypesMode: 'all',
      targetWorkLogTypes: [],
      assetTypesMode: 'all',
      targetAssetTypes: [],
      maintenanceTypesMode: 'all',
      targetMaintenanceTypes: [],
      serviceYearsMode: 'all',
      targetServiceYears: [],
      targetSalesContexts: [],
      salesTargetMode: 'contexts',
      targetPipelineStages: [],
      includeInContractPack: false
    });
    setStageTargetProduct('');
    setCreateNewError('');
    setShowCreateNewModal(true);
  };

  const closeCreateNewModal = () => {
    setShowCreateNewModal(false);
    setCreateNewError('');
  };

  const handleCreateNewTemplate = async () => {
    if (!canEdit) return;

    const name = newTemplateData.name.trim();
    if (!name) {
      setCreateNewError('Template name is required.');
      return;
    }

    try {
      setCreateNewLoading(true);
      setCreateNewError('');

      // Prepare targeting values. Usage is exclusive: a scheduled maintenance
      // template targets ONLY the scheduled_maintenance context (plus visit
      // type/year), a reactive template targets work log types and never
      // appears on maintenance sign-offs. A sales form targets sales contexts
      // (plus an optional asset type) and never a work-log/maintenance context.
      const isSales = newTemplateData.check_category === 'sales_form';
      const isScheduled = !isSales && newTemplateData.usage === 'scheduled';
      const isStageTargeted = isSales && newTemplateData.salesTargetMode === 'stages';

      if (isSales && !isStageTargeted && newTemplateData.targetSalesContexts.length === 0) {
        setCreateNewError('Select at least one activity type for this form.');
        setCreateNewLoading(false);
        return;
      }
      if (isStageTargeted && newTemplateData.targetPipelineStages.length === 0) {
        setCreateNewError('Select at least one pipeline stage for a stage-triggered form.');
        setCreateNewLoading(false);
        return;
      }

      const targetWorkLogTypes = isSales
        ? null
        : (isScheduled
          ? ['scheduled_maintenance']
          : (newTemplateData.workLogTypesMode === 'all'
            ? null
            : (newTemplateData.targetWorkLogTypes.length > 0 ? newTemplateData.targetWorkLogTypes : null)));
      const targetAssetTypes = newTemplateData.assetTypesMode === 'all'
        ? null
        : (newTemplateData.targetAssetTypes.length > 0 ? newTemplateData.targetAssetTypes : null);
      const targetMaintenanceTypes = !isScheduled
        ? null
        : (newTemplateData.maintenanceTypesMode === 'all'
          ? null
          : (newTemplateData.targetMaintenanceTypes.length > 0 ? newTemplateData.targetMaintenanceTypes : null));
      // No years when the Service Years picker is hidden (see Save Draft).
      const targetServiceYears = (!isScheduled || !serviceYearsTargetingApplies(newTemplateData.maintenanceTypesMode, newTemplateData.targetMaintenanceTypes))
        ? null
        : (newTemplateData.serviceYearsMode === 'all'
          ? null
          : (newTemplateData.targetServiceYears.length > 0 ? newTemplateData.targetServiceYears : null));
      const targetSalesContexts = (isSales && !isStageTargeted)
        ? (newTemplateData.targetSalesContexts.length > 0 ? newTemplateData.targetSalesContexts : null)
        : null;
      const targetPipelineStages = isStageTargeted
        ? (newTemplateData.targetPipelineStages.length > 0 ? newTemplateData.targetPipelineStages : null)
        : null;
      const targetEquipmentTypes = newTemplateData.check_category !== 'equipment_check'
        ? null
        : (newTemplateData.equipmentTypesMode === 'all'
          ? null
          : (newTemplateData.targetEquipmentTypes.length > 0 ? newTemplateData.targetEquipmentTypes : null));

      const response = await authFetch('/api/v1/check-form-templates/create-new', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name,
          description: newTemplateData.description.trim(),
          check_category: newTemplateData.check_category,
          target_work_log_types: targetWorkLogTypes,
          target_asset_types: targetAssetTypes,
          target_maintenance_types: targetMaintenanceTypes,
          target_service_years: targetServiceYears,
          target_sales_contexts: targetSalesContexts,
          target_pipeline_stages: targetPipelineStages,
          target_equipment_types: targetEquipmentTypes,
          include_in_contract_pack: isStageTargeted && newTemplateData.includeInContractPack
        })
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 403) {
          setCreateNewError('You do not have permission to create templates.');
        } else {
          setCreateNewError(data.message || 'Failed to create template');
        }
        return;
      }

      // Close modal and open the newly created draft in the editor
      closeCreateNewModal();
      setSelectedTemplate(null); // Clear any selected template
      // A brand-new form copies nothing, so the variant notice ("based on an
      // existing one") does not apply; nothing in it is published yet.
      enterDraftEditor(data, false, null);
      setSaveMessage({ type: 'success', text: `Template "${name}" created as a draft. Add sections and fields, then publish when ready.` });

      // Refresh the drafts map
      const draftsResponse = await authFetch('/api/v1/check-form-templates?status=draft');
      if (draftsResponse.ok) {
        const draftsData = await draftsResponse.json();
        const drafts = {};
        (draftsData.data || []).forEach(draft => {
          drafts[draft.template_key] = draft;
        });
        setDraftsMap(drafts);
      }
    } catch (err) {
      setCreateNewError(err.message || 'Failed to create template');
    } finally {
      setCreateNewLoading(false);
    }
  };

  const handleOpenExistingDraft = async () => {
    if (!existingDraftInfo?.draftId) return;

    try {
      setDetailLoading(true);
      setDetailError(null);
      setExistingDraftInfo(null);

      const response = await authFetch(`/api/v1/check-form-templates/${existingDraftInfo.draftId}`);
      if (!response.ok) {
        throw new Error('Failed to load existing draft');
      }

      const draft = await response.json();
      enterDraftEditor(draft);
    } catch (err) {
      setDetailError(err.message || 'Failed to load draft');
    } finally {
      setDetailLoading(false);
    }
  };

  const sortFieldsByOrder = (schema) => {
    if (!schema?.sections) return schema;

    return {
      ...schema,
      sections: schema.sections.map(section => ({
        ...section,
        fields: [...(section.fields || [])].sort((a, b) => {
          const orderA = a.order ?? Infinity;
          const orderB = b.order ?? Infinity;
          return orderA - orderB;
        })
      }))
    };
  };

  // publishedFrom: for a draft just made, the schema whose keys are already
  // published (null for a new form or a variant: none are). A draft read
  // from the server says itself (published_keys).
  const enterDraftEditor = (draft, newVariant = false, publishedFrom = undefined) => {
    if (draft.published_keys !== undefined) {
      setDraftPublishedKeys(draft.published_keys
        ? { sections: new Set(draft.published_keys.sections || []), fields: new Set(draft.published_keys.fields || []) }
        : null);
    } else if (publishedFrom !== undefined) {
      setDraftPublishedKeys(keysOfSchema(publishedFrom));
    } else {
      setDraftPublishedKeys(null);
    }
    setDraftTemplate(draft);
    const schema = JSON.parse(JSON.stringify(draft.schema_json));
    setDraftSchema(sortFieldsByOrder(schema));
    setDraftName(draft.name);
    setIsEditingDraft(true);
    setValidationErrors([]);
    setSaveMessage(null);
    setDetailView('fields');
    setTargetingConflict(null);
    setIsNewVariant(newVariant);

    setDraftCheckCategory(draft.check_category || null);
    const scheduled = isScheduledMaintenanceTemplate(draft.target_work_log_types);
    setDraftUsage(scheduled ? 'scheduled' : 'reactive');
    // For scheduled templates the work-log targeting is implicit
    // (['scheduled_maintenance']); only reactive work log types are editable.
    const reactiveTypes = scheduled
      ? null
      : draft.target_work_log_types;
    setDraftWorkLogTypes(reactiveTypes);
    setDraftAssetTypes(draft.target_asset_types);
    setDraftMaintenanceTypes(draft.target_maintenance_types ?? null);
    setDraftServiceYears(draft.target_service_years ?? null);
    setWorkLogTypesMode(reactiveTypes === null ? 'all' : 'specific');
    setAssetTypesMode(draft.target_asset_types === null ? 'all' : 'specific');
    setMaintenanceTypesMode((draft.target_maintenance_types ?? null) === null ? 'all' : 'specific');
    setServiceYearsMode((draft.target_service_years ?? null) === null ? 'all' : 'specific');
    setDraftSalesContexts(draft.target_sales_contexts ?? null);
    if (draft.check_category === 'sales_form' && salesContextOptions.length === 0) {
      loadSalesContexts();
    }
    setDraftEquipmentTypes(draft.target_equipment_types ?? null);
    setEquipmentTypesMode((draft.target_equipment_types ?? null) === null ? 'all' : 'specific');
    if (draft.check_category === 'equipment_check' && equipmentTypeOptions.length === 0) {
      loadEquipmentTypes();
    }
  };

  // The draft the detail header's "Open Draft" button opens: the template
  // itself when it is a draft (a brand-new template or a variant has no active
  // parent to reach it from), otherwise the draft already waiting on an active
  // template. Null when there is none or the viewer cannot edit. The footer
  // hint reads the same answer, so it only names a button that is on screen.
  const getOpenableDraftId = (template) => {
    if (!canEdit || !template) return null;
    if (template.status === 'draft') return template.template_id;
    if (template.status === 'active') return draftsMap[template.template_key]?.template_id || null;
    return null;
  };

  const getDetailFooterHint = (template) => {
    if (!canEdit) {
      return 'This is a read-only view. Contact an administrator to make changes.';
    }
    if (getOpenableDraftId(template)) {
      return 'Click "Open Draft" to continue editing. Live forms will not be affected until the draft is published.';
    }
    if (template.status === 'active') {
      return 'Click "Create Draft" to make changes. Live forms will not be affected until the draft is published.';
    }
    if (template.status === 'superseded') {
      return 'This is an older version. Make changes on the active version, or click "Create Variant" to start a new template from this one.';
    }
    if (template.status === 'archived') {
      return 'This template is archived. Click "Restore" to make it active again before making changes.';
    }
    return null;
  };

  // The note under a template's targeting names the same buttons as the footer
  // hint. It is left out for users who cannot edit: their footer already says
  // the whole view is read-only.
  const getTargetingNote = (template) => {
    if (!canEdit) return null;
    if (getOpenableDraftId(template)) {
      return 'Click "Open Draft" to change targeting in the draft.';
    }
    if (template.status === 'active') {
      return 'Targeting cannot be changed on the active version. Click "Create Draft" to change it in a new draft.';
    }
    if (template.status === 'superseded') {
      return 'Targeting cannot be changed on an older version. Change it on the active version, or click "Create Variant" to start a new template from this one.';
    }
    if (template.status === 'archived') {
      return 'Targeting cannot be changed on an archived template. Click "Restore" to make it active again first.';
    }
    return null;
  };

  const handleOpenDraft = async (draftId) => {
    if (!draftId) return;

    try {
      setDetailLoading(true);
      setDetailError(null);

      const response = await authFetch(`/api/v1/check-form-templates/${draftId}`);
      if (!response.ok) {
        throw new Error('Failed to load draft');
      }

      const fullDraft = await response.json();
      enterDraftEditor(fullDraft);
    } catch (err) {
      setDetailError(err.message || 'Failed to load draft');
    } finally {
      setDetailLoading(false);
    }
  };

  // Writes what is on screen to the draft. Returns the saved draft, or null
  // when the save failed (its error is already on screen). Save Draft and
  // Publish Draft both go through here.
  const saveDraftChanges = async () => {
    try {
      setSaving(true);
      setSaveMessage(null);
      setValidationErrors([]);

      // The live form's heading is the schema's own name: it follows the template name.
      const updatedSchema = { ...draftSchema, name: draftName };

      // Sales forms have no reactive/scheduled dimensions - they target sales
      // contexts (or pipeline stages, which are fixed at creation).
      const isSalesDraft = draftCheckCategory === 'sales_form';
      const isStageTargetedDraft = isSalesDraft
        && Array.isArray(draftTemplate.target_pipeline_stages)
        && draftTemplate.target_pipeline_stages.length > 0;

      const isScheduled = draftUsage === 'scheduled';
      const targetWorkLogTypes = isScheduled
        ? ['scheduled_maintenance']
        : (workLogTypesMode === 'all' ? null :
          (Array.isArray(draftWorkLogTypes) && draftWorkLogTypes.length > 0 ? draftWorkLogTypes : null));
      const targetAssetTypes = assetTypesMode === 'all' ? null :
        (Array.isArray(draftAssetTypes) && draftAssetTypes.length > 0 ? draftAssetTypes : null);
      const targetMaintenanceTypes = !isScheduled ? null :
        (maintenanceTypesMode === 'all' ? null :
          (Array.isArray(draftMaintenanceTypes) && draftMaintenanceTypes.length > 0 ? draftMaintenanceTypes : null));
      // No years once the Service Years picker is hidden (visit types without
      // annual inspections): years left over from before silently narrowed
      // the form to those contract years.
      const targetServiceYears = (!isScheduled || !serviceYearsTargetingApplies(maintenanceTypesMode, draftMaintenanceTypes)) ? null :
        (serviceYearsMode === 'all' ? null :
          (Array.isArray(draftServiceYears) && draftServiceYears.length > 0 ? draftServiceYears : null));
      // Equipment targeting only applies to equipment_check drafts.
      const isEquipmentDraft = draftCheckCategory === 'equipment_check';
      const targetEquipmentTypes = !isEquipmentDraft ? null :
        (equipmentTypesMode === 'all' ? null :
          (Array.isArray(draftEquipmentTypes) && draftEquipmentTypes.length > 0 ? draftEquipmentTypes : null));

      if (!isScheduled && workLogTypesMode === 'specific' && (!targetWorkLogTypes || targetWorkLogTypes.length === 0)) {
        setWorkLogTypesMode('all');
      }
      if (assetTypesMode === 'specific' && (!targetAssetTypes || targetAssetTypes.length === 0)) {
        setAssetTypesMode('all');
      }
      if (maintenanceTypesMode === 'specific' && (!targetMaintenanceTypes || targetMaintenanceTypes.length === 0)) {
        setMaintenanceTypesMode('all');
      }
      if (serviceYearsMode === 'specific' && (!targetServiceYears || targetServiceYears.length === 0)) {
        setServiceYearsMode('all');
      }
      if (equipmentTypesMode === 'specific' && (!targetEquipmentTypes || targetEquipmentTypes.length === 0)) {
        setEquipmentTypesMode('all');
      }

      const response = await authFetch(`/api/v1/check-form-templates/${draftTemplate.template_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: draftName,
          schema_json: updatedSchema,
          check_category: draftCheckCategory,
          target_work_log_types: isSalesDraft ? null : targetWorkLogTypes,
          target_asset_types: isSalesDraft ? null : targetAssetTypes,
          target_maintenance_types: isSalesDraft ? null : targetMaintenanceTypes,
          target_service_years: isSalesDraft ? null : targetServiceYears,
          // Context-mode sales drafts save their (dynamic-list) contexts;
          // stage-targeted ones keep contexts null; non-sales clear them.
          target_sales_contexts: (isSalesDraft && !isStageTargetedDraft)
            ? (Array.isArray(draftSalesContexts) && draftSalesContexts.length > 0 ? draftSalesContexts : null)
            : null,
          target_equipment_types: targetEquipmentTypes
        })
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.error === 'Schema Validation Error') {
          setValidationErrors([{
            message: data.message,
            field: data.field,
            section: data.section
          }]);
        } else {
          throw new Error(data.message || 'Failed to save draft');
        }
        return null;
      }

      setDraftTemplate(data);
      const schema = JSON.parse(JSON.stringify(data.schema_json));
      setDraftSchema(sortFieldsByOrder(schema));
      setDraftName(data.name);
      // A draft opened from its own card is also the template shown behind the
      // editor: keep that view and its list card on the saved version, so
      // leaving the editor never shows the pre-save copy.
      setSelectedTemplate(prev => (prev?.template_id === data.template_id ? { ...prev, ...data } : prev));
      setTemplates(prev => prev.map(t => (t.template_id === data.template_id ? { ...t, ...data } : t)));
      return data;
    } catch (err) {
      setSaveMessage({ type: 'error', text: err.message || 'Failed to save draft' });
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!draftTemplate || !canEdit) return;

    const saved = await saveDraftChanges();
    if (saved) {
      setSaveMessage({ type: 'success', text: 'Draft saved. This draft is not live yet and does not affect live check forms.' });
    }
  };

  const handleDiscardDraft = async () => {
    if (!draftTemplate || !canEdit) return;

    try {
      setSaving(true);
      setSaveMessage(null);

      const response = await authFetch(`/api/v1/check-form-templates/${draftTemplate.template_id}/draft`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to discard draft');
      }

      setIsEditingDraft(false);
      setDraftTemplate(null);
      setDraftSchema(null);
      setDraftName('');
      setShowDiscardConfirm(false);
      setValidationErrors([]);

      // The discarded draft is gone for good. When it was the template on
      // show (opened from its own card), clear the view; either way reload the
      // list and draftsMap so its card and its Open Draft button go too.
      if (selectedTemplate?.template_id === draftTemplate.template_id) {
        setSelectedTemplate(null);
      }
      await fetchTemplates();
    } catch (err) {
      setSaveMessage({ type: 'error', text: err.message || 'Failed to discard draft' });
    } finally {
      setSaving(false);
    }
  };

  const handleExitDraftEditor = () => {
    // A brand-new template or a variant has nothing on screen to reach it
    // from (no template is selected, or the one selected is the template it
    // was copied from), so show the draft being left with its Open Draft button.
    if (draftTemplate && selectedTemplate?.template_key !== draftTemplate.template_key) {
      setSelectedTemplate(draftTemplate);
    }
    setIsEditingDraft(false);
    setDraftTemplate(null);
    setDraftSchema(null);
    setDraftName('');
    setValidationErrors([]);
    setSaveMessage(null);
    setPublishErrors([]);
    setTargetingConflict(null);
    setDraftCheckCategory(null);
    setDraftUsage('reactive');
    setDraftWorkLogTypes(null);
    setDraftAssetTypes(null);
    setDraftMaintenanceTypes(null);
    setDraftServiceYears(null);
    setWorkLogTypesMode('all');
    setAssetTypesMode('all');
    setMaintenanceTypesMode('all');
    setServiceYearsMode('all');
    // Pick up drafts made or changed in the editor: their list cards, and the
    // draftsMap that decides between Create Draft and Open Draft.
    fetchTemplates();
  };

  const handlePublishDraft = async () => {
    if (!draftTemplate || !canEdit) return;

    try {
      setPublishing(true);
      setSaveMessage(null);
      setPublishErrors([]);
      setTargetingConflict(null);
      setShowPublishConfirm(false);

      // Publish puts the draft live as it is stored, so save what is on screen
      // first. Unsaved edits (e.g. Used For switched to Scheduled maintenance)
      // used to be left out of the published form and then thrown away.
      const saved = await saveDraftChanges();
      if (!saved) return;

      const response = await authFetch(`/api/v1/check-form-templates/${saved.template_id}/publish`, {
        method: 'POST'
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.error === 'Publish Validation Error' && data.validation_errors) {
          setPublishErrors(data.validation_errors);
          setSaveMessage({ type: 'error', text: 'Cannot publish: Please fix the validation errors below.' });
        } else if (data.error === 'Targeting Conflict' && data.conflicting_templates) {
          setTargetingConflict(data);
          setSaveMessage({ type: 'error', text: 'Cannot publish: Targeting overlaps with another active template.' });
        } else if (response.status === 403) {
          setSaveMessage({ type: 'error', text: 'You do not have permission to publish templates.' });
        } else {
          setSaveMessage({ type: 'error', text: data.message || 'Failed to publish template' });
        }
        return;
      }

      const publishedName = data.template?.name || draftName;

      setIsEditingDraft(false);
      setDraftTemplate(null);
      setDraftSchema(null);
      setDraftName('');
      setPublishErrors([]);

      await fetchTemplates();

      if (data.template) {
        setSelectedTemplate(data.template);
      }

      setSaveMessage({ type: 'success', text: `Template "${publishedName}" published successfully and is now active in Settings.` });
    } catch (err) {
      setSaveMessage({ type: 'error', text: err.message || 'Failed to publish template' });
    } finally {
      setPublishing(false);
    }
  };

  // Archiving the only form for a work type used to remove the check without
  // a word (Gareth 2026-10-21): the confirmation now names what would be left
  // with no form at all.
  const openArchiveConfirm = async () => {
    if (!selectedTemplate) return;
    setShowArchiveConfirm(true);
    if (selectedTemplate.status !== 'active') {
      setArchiveImpact(null);
      return;
    }
    setArchiveImpact({ loading: true, uncovered: [], failed: false });
    try {
      const response = await authFetch(`/api/v1/check-form-templates/${selectedTemplate.template_id}/archive-impact`);
      const data = await response.json().catch(() => ({}));
      setArchiveImpact(response.ok
        ? { loading: false, uncovered: data.uncovered || [], failed: false }
        : { loading: false, uncovered: [], failed: true });
    } catch (err) {
      setArchiveImpact({ loading: false, uncovered: [], failed: true });
    }
  };

  const loadFormHistory = async (templateId) => {
    setFormHistory({ templateId, loading: true, rows: [], failed: false });
    try {
      const response = await authFetch(`/api/v1/check-form-templates/${templateId}/history`);
      const data = await response.json().catch(() => ({}));
      setFormHistory(response.ok
        ? { templateId, loading: false, rows: data.data || [], failed: false }
        : { templateId, loading: false, rows: [], failed: true });
    } catch (err) {
      setFormHistory({ templateId, loading: false, rows: [], failed: true });
    }
  };

  const handleArchiveTemplate = async () => {
    if (!selectedTemplate || !canEdit) return;

    try {
      setArchiving(true);
      setSaveMessage(null);
      setShowArchiveConfirm(false);

      const response = await authFetch(`/api/v1/check-form-templates/${selectedTemplate.template_id}/archive`, {
        method: 'POST'
      });

      const data = await response.json();

      if (!response.ok) {
        setSaveMessage({ type: 'error', text: data.message || 'Failed to archive template' });
        return;
      }

      const archivedName = selectedTemplate.name;
      setSelectedTemplate(null);
      await fetchTemplates();
      setSaveMessage({ type: 'success', text: `Template "${archivedName}" has been archived.` });
    } catch (err) {
      setSaveMessage({ type: 'error', text: err.message || 'Failed to archive template' });
    } finally {
      setArchiving(false);
    }
  };

  const handleRestoreTemplate = async () => {
    if (!selectedTemplate || !canEdit) return;

    try {
      setRestoring(true);
      setSaveMessage(null);
      setRestoreConflict(null);

      const response = await authFetch(`/api/v1/check-form-templates/${selectedTemplate.template_id}/restore`, {
        method: 'POST'
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 409 && data.conflicting_templates) {
          setRestoreConflict(data);
          setSaveMessage({ type: 'error', text: 'Cannot restore: Template targeting conflicts with another active template.' });
        } else {
          setSaveMessage({ type: 'error', text: data.message || 'Failed to restore template' });
        }
        return;
      }

      const restoredName = selectedTemplate.name;
      setSelectedTemplate(data.template || null);
      await fetchTemplates();
      setSaveMessage({ type: 'success', text: `Template "${restoredName}" has been restored to active.` });
    } catch (err) {
      setSaveMessage({ type: 'error', text: err.message || 'Failed to restore template' });
    } finally {
      setRestoring(false);
    }
  };

  const openRenameModal = () => {
    if (!selectedTemplate) return;
    setRenameValue(selectedTemplate.name);
    setRenameError('');
    setShowRenameModal(true);
  };

  const closeRenameModal = () => {
    setShowRenameModal(false);
    setRenameValue('');
    setRenameError('');
  };

  const handleRenameTemplate = async () => {
    if (!selectedTemplate || !canEdit) return;

    const trimmedName = renameValue.trim();
    if (!trimmedName) {
      setRenameError('Template name is required.');
      return;
    }

    if (trimmedName === selectedTemplate.name) {
      closeRenameModal();
      return;
    }

    try {
      setRenaming(true);
      setRenameError('');

      const response = await authFetch(`/api/v1/check-form-templates/${selectedTemplate.template_id}/rename`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: trimmedName })
      });

      const data = await response.json();

      if (!response.ok) {
        setRenameError(data.message || 'Failed to rename template');
        return;
      }

      closeRenameModal();
      setSelectedTemplate(data.template || { ...selectedTemplate, name: trimmedName });
      await fetchTemplates();
      setSaveMessage({ type: 'success', text: `Template renamed to "${trimmedName}".` });
    } catch (err) {
      setRenameError(err.message || 'Failed to rename template');
    } finally {
      setRenaming(false);
    }
  };

  const handleStatusFilterChange = (newFilter) => {
    if (newFilter === statusFilter) return;
    setStatusFilter(newFilter);
    setSelectedTemplate(null);
    fetchTemplates(newFilter);
  };

  const updateSchemaDescription = (value) => {
    setDraftSchema(prev => ({ ...prev, description: value }));
  };

  const countFields = (schemaJson, { activeOnly = false } = {}) => {
    if (activeOnly) {
      return (schemaJson?.sections || []).filter(section => !section.archived).reduce((sum, section) => {
        const fields = (section.fields || []).filter(field => !field.archived);
        return sum + fields.length + fields.filter(field => field.follow_up?.field && !field.follow_up.field.archived).length;
      }, 0);
    }
    if (!schemaJson?.sections) return 0;
    return schemaJson.sections.reduce((sum, section) => {
      const sectionFields = section.fields?.length || 0;
      const followUpFields = section.fields?.filter(f => f.follow_up?.field).length || 0;
      return sum + sectionFields + followUpFields;
    }, 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  // Archived sections, and archived questions in live sections: kept for
  // history, never on the live form.
  const archivedCountOf = (schemaJson) => (schemaJson?.sections || []).reduce((sum, section) => (
    sum + (section.archived ? 1 : (section.fields || []).filter(field => field.archived).length)
  ), 0);

  const renderFieldDetails = (field, isFollowUp = false) => (
    <div key={field.field_key} className={`check-field-row ${isFollowUp ? 'follow-up' : ''} ${field.archived ? 'archived' : ''}`}>
      <div className="check-field-main">
        <div className="check-field-label">
          {field.label}
          {field.archived && <span className="archived-badge">Archived</span>}
        </div>
        <div className="check-field-meta">
          <span className="field-key">{field.field_key}</span>
          <span className="field-type">{TYPE_LABELS[field.field_type] || field.field_type}</span>
        </div>
      </div>
      <div className="check-field-flags">
        {field.required_on_save && <span className="flag save">Save</span>}
        {field.required_on_complete && <span className="flag complete">Complete</span>}
        {field.allow_photos && <span className="flag photos">Photos</span>}
        {field.system_field && <span className="flag system">System</span>}
        {field.inverted_logic && <span className="flag inverted">Inverted</span>}
      </div>
      <div className="check-field-values">
        {field.pass_value && <span className="pass-value">Pass: {field.pass_value}</span>}
        {field.fail_value && <span className="fail-value">Fail: {field.fail_value}</span>}
      </div>
    </div>
  );

  if (loading) {
    return <div className="check-forms-loading">Loading check form templates...</div>;
  }

  if (error) {
    return (
      <div className="check-forms-error">
        <p>{error}</p>
        <button type="button" className="btn-secondary" onClick={fetchTemplates}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="check-forms-settings">
      <div className="coverage-matrix-section">
        <div className="coverage-header">
          <h3>Scheduled Maintenance Coverage</h3>
          <button
            type="button"
            className="btn-secondary"
            onClick={fetchCoverage}
            disabled={coverageLoading}
          >
            {coverageLoading ? 'Checking...' : coverage ? 'Refresh Coverage' : 'Show Coverage'}
          </button>
        </div>
        {coverage && (
          <div className="coverage-table-wrap">
            <table className="coverage-table">
              <thead>
                <tr>
                  <th>Asset Type</th>
                  {coverage.contexts.map(c => <th key={c.key}>{c.label}</th>)}
                </tr>
              </thead>
              <tbody>
                {coverage.data.map(row => (
                  <tr key={row.asset_type}>
                    <td className="coverage-asset">{row.label}</td>
                    {coverage.contexts.map(c => {
                      const cell = row.cells[c.key];
                      return (
                        <td key={c.key} className={`coverage-cell ${cell.status}`}
                          title={cell.status === 'ok' ? cell.template_name
                            : cell.status === 'ambiguous' ? `${cell.conflict_count} templates tie - fix targeting`
                            : 'No active form - sign-off dead-end'}>
                          {cell.status === 'ok' ? '✓' : cell.status === 'ambiguous' ? '⚠' : '✗'}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="coverage-legend">
              <span><span className="coverage-cell ok">✓</span> covered</span>
              <span><span className="coverage-cell missing">✗</span> missing (sign-off dead-end)</span>
              <span><span className="coverage-cell ambiguous">⚠</span> ambiguous targeting</span>
            </div>
            {Array.isArray(coverage.equipment) && coverage.equipment.length > 0 && (
              <table className="coverage-table" style={{ marginTop: '12px' }}>
                <thead>
                  <tr>
                    <th>Equipment Type</th>
                    <th>Check Frequency</th>
                    <th>Active Form</th>
                  </tr>
                </thead>
                <tbody>
                  {coverage.equipment.map(row => (
                    <tr key={row.equipment_type}>
                      <td className="coverage-asset">{row.label}</td>
                      <td>{String(row.check_frequency || '').replace(/_/g, ' ')}</td>
                      <td className={`coverage-cell ${row.status}`}
                        title={row.status === 'ok' ? row.template_name
                          : row.status === 'ambiguous' ? `${row.conflict_count} templates tie - fix targeting`
                          : 'No active form for this equipment type'}>
                        {row.status === 'ok' ? `✓ ${row.template_name}` : row.status === 'ambiguous' ? '⚠' : '✗'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>


      <EquipmentTypeSettings />

      {/* While a draft is open the list (disabled then) steps aside, so the
          designer's three columns get the page's width. */}
      <div className={`check-forms-layout ${isEditingDraft ? 'editing-draft' : ''}`}>
        <div className="check-forms-list">
          <div className="list-header">
            <div className="list-header-left">
              <h3>Check Form Templates</h3>
              <span className="list-count">{templates.length} template{templates.length !== 1 ? 's' : ''}</span>
            </div>
            {canEdit && (
              <button
                type="button"
                className="btn-primary btn-create-new"
                onClick={openCreateNewModal}
                disabled={isEditingDraft}
                title={isEditingDraft ? 'Finish editing current draft first' : 'Create a new check form template'}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19"/>
                  <line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
                Create New Template
              </button>
            )}
          </div>

          <div className="status-filter-tabs">
            <button
              type="button"
              className={`status-filter-tab ${statusFilter === 'active' ? 'active' : ''}`}
              onClick={() => handleStatusFilterChange('active')}
              disabled={isEditingDraft}
            >
              Active
            </button>
            <button
              type="button"
              className={`status-filter-tab ${statusFilter === 'draft' ? 'active' : ''}`}
              onClick={() => handleStatusFilterChange('draft')}
              disabled={isEditingDraft}
            >
              Drafts
            </button>
            <button
              type="button"
              className={`status-filter-tab ${statusFilter === 'archived' ? 'active' : ''}`}
              onClick={() => handleStatusFilterChange('archived')}
              disabled={isEditingDraft}
            >
              Archived
            </button>
            <button
              type="button"
              className={`status-filter-tab ${statusFilter === 'all' ? 'active' : ''}`}
              onClick={() => handleStatusFilterChange('all')}
              disabled={isEditingDraft}
            >
              All
            </button>
          </div>

          {templates.length === 0 ? (
            <div className="empty-state">
              <p>No {statusFilter === 'all' ? '' : statusFilter + ' '}check form templates found.</p>
              {canEdit && statusFilter === 'active' && (
                <button
                  type="button"
                  className="btn-primary"
                  onClick={openCreateNewModal}
                >
                  Create New Template
                </button>
              )}
            </div>
          ) : (
            <div className="template-cards">
              {templates.map((template) => {
                // A draft's own card is already marked Draft: the extra badge is for an
                // active form with a draft waiting.
                const hasDraft = template.status !== 'draft' && !!draftsMap[template.template_key];
                const isArchivedTemplate = template.status === 'archived';
                const workLogDisplay = template.target_work_log_types === null
                  ? 'All work types'
                  : template.target_work_log_types.map(v => getWorkLogTypeLabel(v, workLogTypeOptions)).join(', ');
                const assetDisplay = template.target_asset_types === null
                  ? 'All assets'
                  : template.target_asset_types.map(getAssetTypeLabel).join(', ');
                return (
                  <button
                    key={template.template_id}
                    type="button"
                    className={`template-card ${selectedTemplate?.template_id === template.template_id && !isEditingDraft ? 'selected' : ''} ${isEditingDraft ? 'disabled' : ''} ${hasDraft ? 'has-draft' : ''} ${isArchivedTemplate ? 'archived' : ''}`}
                    onClick={() => handleSelectTemplate(template)}
                    disabled={isEditingDraft}
                  >
                    <div className="template-card-header">
                      <span className="template-name">{template.name}</span>
                      <div className="template-card-badges">
                        <span className={`status-badge ${template.status}`}>{STATUS_LABELS[template.status]}</span>
                        {hasDraft && <span className="status-badge draft-indicator">Draft</span>}
                      </div>
                    </div>
                    <div className="template-card-meta">
                      <span className="meta-item">
                        <span className="meta-label">Type:</span>
                        <span className="meta-value">{formTypeLabel(template.form_type)}</span>
                      </span>
                      <span className="meta-item">
                        <span className="meta-label">Version:</span>
                        <span className="meta-value">v{template.version}</span>
                      </span>
                      {template.is_default && (
                        <span className="default-badge">Default</span>
                      )}
                    </div>
                    <div className="template-card-targeting">
                      {template.check_category && (
                        <span className="targeting-item">
                          <span className="targeting-label">Category:</span>
                          <span className="targeting-value">{getCategoryLabel(template.check_category)}</span>
                        </span>
                      )}
                      <span className="targeting-item">
                        <span className="targeting-label">Work:</span>
                        <span className="targeting-value">{workLogDisplay}</span>
                      </span>
                      <span className="targeting-item">
                        <span className="targeting-label">Assets:</span>
                        <span className="targeting-value">{assetDisplay}</span>
                      </span>
                    </div>
                    <div className="template-card-footer">
                      <span className="meta-item">
                        <span className="meta-label">Published:</span>
                        <span className="meta-value">{formatDate(template.published_at)}</span>
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="check-forms-detail">
          {/* Draft Editor View */}
          {isEditingDraft && draftTemplate && draftSchema && (
            <div className="template-detail draft-mode">
              <div className="detail-header draft-header">
                <div className="draft-header-left">
                  <span className="status-badge draft">Editing Draft</span>
                  <h3>{draftName}</h3>
                </div>
                <div className="detail-header-meta">
                  <span className="version-badge">v{draftTemplate.version}</span>
                  <span className="form-type-label">{formTypeLabel(draftTemplate.form_type)}</span>
                </div>
              </div>

              <div className="draft-notice">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="16" x2="12" y2="12"/>
                  <line x1="12" y1="8" x2="12.01" y2="8"/>
                </svg>
                <span>You are editing a draft. Save Draft keeps your changes. Live forms will not be affected until you publish the saved draft with "Publish Draft".</span>
              </div>

              {saveMessage && (
                <div className={`save-message ${saveMessage.type}`}>
                  {saveMessage.text}
                </div>
              )}

              {validationErrors.length > 0 && (
                <div className="validation-errors">
                  <strong>Validation Errors:</strong>
                  <ul>
                    {validationErrors.map((err, idx) => (
                      <li key={idx}>
                        {err.message}
                        {err.field && <span className="error-field"> (field: {err.field})</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="detail-view-tabs">
                <button
                  type="button"
                  className={`view-tab ${detailView === 'fields' ? 'active' : ''}`}
                  onClick={() => setDetailView('fields')}
                >
                  Edit Fields
                </button>
                <button
                  type="button"
                  className={`view-tab ${detailView === 'preview' ? 'active' : ''}`}
                  onClick={() => setDetailView('preview')}
                >
                  Preview
                </button>
              </div>

              {isNewVariant && (
                <div className="new-variant-notice">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="12" y1="8" x2="12" y2="12"/>
                    <line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                  <div className="new-variant-content">
                    <span className="new-variant-title">New Template Variant</span>
                    <span className="new-variant-desc">
                      This is a new template based on an existing one. It starts with the same targeting as the source.
                      <strong> Change the targeting before publishing</strong> if it overlaps with the source template,
                      or publishing will fail with a targeting conflict.
                    </span>
                  </div>
                </div>
              )}

              {detailView === 'fields' && (
                <div className="draft-editor-content">
                  <div className="draft-metadata-section">
                    <h4>Template Settings</h4>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Template Name</label>
                        <input
                          type="text"
                          value={draftName}
                          onChange={(e) => setDraftName(e.target.value)}
                        />
                      </div>
                      <div className="form-group">
                        <label>Description</label>
                        <input
                          type="text"
                          value={draftSchema.description || ''}
                          onChange={(e) => updateSchemaDescription(e.target.value)}
                          placeholder="Optional description"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="draft-targeting-section">
                    <h4>Targeting</h4>
                    <p className="targeting-hint">
                      Configure when this template should be used. These settings determine which checks show this form.
                    </p>

                    <div className="targeting-row">
                      <div className="form-group">
                        <label>Check Category</label>
                        <select
                          value={draftCheckCategory || ''}
                          onChange={(e) => {
                            const value = e.target.value || null;
                            setDraftCheckCategory(value);
                            if (value === 'sales_form' && salesContextOptions.length === 0) {
                              loadSalesContexts();
                            }
                            if (value === 'equipment_check' && equipmentTypeOptions.length === 0) {
                              loadEquipmentTypes();
                            }
                          }}
                        >
                          <option value="">Select category...</option>
                          {CHECK_CATEGORIES.map(cat => (
                            <option key={cat.value} value={cat.value}>{cat.label}</option>
                          ))}
                        </select>
                        <span className="form-hint">
                          {CHECK_CATEGORIES.find(c => c.value === draftCheckCategory)?.description || 'Select a category'}
                        </span>
                      </div>
                    </div>

                    {draftCheckCategory !== 'sales_form' && (
                    <>
                    <div className="targeting-row">
                      <div className="form-group">
                        <label>Used For</label>
                        <span className="targeting-hint">Where this form is used. A form is for one or the other, never both.</span>
                        <div className="targeting-mode-selector">
                          <label className="radio-label">
                            <input
                              type="radio"
                              name="draftUsage"
                              checked={draftUsage === 'reactive'}
                              onChange={() => setDraftUsage('reactive')}
                            />
                            <span>Reactive work: tickets and all other jobs</span>
                          </label>
                          <label className="radio-label">
                            <input
                              type="radio"
                              name="draftUsage"
                              checked={draftUsage === 'scheduled'}
                              onChange={() => setDraftUsage('scheduled')}
                            />
                            <span>Scheduled maintenance: Log Work on Quarterly Health Check and Annual Service jobs, and maintenance sign-off</span>
                          </label>
                        </div>
                      </div>
                    </div>

                    {draftUsage === 'reactive' && (
                    <div className="targeting-row">
                      <div className="form-group">
                        <label>Work Log Types</label>
                        <div className="targeting-mode-selector">
                          <label className="radio-label">
                            <input
                              type="radio"
                              name="workLogTypesMode"
                              checked={workLogTypesMode === 'all'}
                              onChange={() => {
                                setWorkLogTypesMode('all');
                                setDraftWorkLogTypes(null);
                              }}
                            />
                            <span>All work log types</span>
                          </label>
                          <label className="radio-label">
                            <input
                              type="radio"
                              name="workLogTypesMode"
                              checked={workLogTypesMode === 'specific'}
                              onChange={() => {
                                setWorkLogTypesMode('specific');
                                setDraftWorkLogTypes([]);
                              }}
                            />
                            <span>Specific work log types</span>
                          </label>
                        </div>
                        {workLogTypesMode === 'specific' && (
                          <div className="targeting-checkboxes">
                            {getWorkLogTypeCheckboxOptions(draftWorkLogTypes).map(type => (
                              <label key={type.value} className="checkbox-label">
                                <input
                                  type="checkbox"
                                  checked={draftWorkLogTypes?.includes(type.value) || false}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setDraftWorkLogTypes(prev => [...(prev || []), type.value]);
                                    } else {
                                      setDraftWorkLogTypes(prev => (prev || []).filter(v => v !== type.value));
                                    }
                                  }}
                                />
                                <span>{type.label}</span>
                              </label>
                            ))}
                            {workLogTypesMode === 'specific' && (!draftWorkLogTypes || draftWorkLogTypes.length === 0) && (
                              <span className="targeting-warning">No work log types selected. "All work log types" will be used on save.</span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    )}

                    <div className="targeting-row">
                      <div className="form-group">
                        <label>Asset Types</label>
                        <div className="targeting-mode-selector">
                          <label className="radio-label">
                            <input
                              type="radio"
                              name="assetTypesMode"
                              checked={assetTypesMode === 'all'}
                              onChange={() => {
                                setAssetTypesMode('all');
                                setDraftAssetTypes(null);
                              }}
                            />
                            <span>All asset types / Not asset-specific</span>
                          </label>
                          <label className="radio-label">
                            <input
                              type="radio"
                              name="assetTypesMode"
                              checked={assetTypesMode === 'specific'}
                              onChange={() => {
                                setAssetTypesMode('specific');
                                setDraftAssetTypes([]);
                              }}
                            />
                            <span>Specific asset types</span>
                          </label>
                        </div>
                        {assetTypesMode === 'specific' && (
                          <div className="targeting-asset-groups">
                            {assetTypeGroups.map(group => (
                              <div key={group.label} className="asset-type-group">
                                <span className="asset-group-label">{group.label}</span>
                                <div className="targeting-checkboxes">
                                  {group.types.map(type => (
                                    <label key={type.value} className="checkbox-label">
                                      <input
                                        type="checkbox"
                                        checked={draftAssetTypes?.includes(type.value) || false}
                                        onChange={(e) => {
                                          if (e.target.checked) {
                                            setDraftAssetTypes(prev => [...(prev || []), type.value]);
                                          } else {
                                            setDraftAssetTypes(prev => (prev || []).filter(v => v !== type.value));
                                          }
                                        }}
                                      />
                                      <span>{type.label}</span>
                                    </label>
                                  ))}
                                </div>
                              </div>
                            ))}
                            {assetTypesMode === 'specific' && (!draftAssetTypes || draftAssetTypes.length === 0) && (
                              <span className="targeting-warning">No asset types selected. "All asset types" will be used on save.</span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {draftCheckCategory === 'equipment_check' && (
                      <div className="targeting-row">
                        <div className="form-group">
                          <label>Equipment Types</label>
                          <span className="targeting-hint">Which registered equipment (vans, toolboxes, hired kit) presents this form. Types are configured in Settings.</span>
                          <div className="targeting-mode-selector">
                            <label className="radio-label">
                              <input
                                type="radio"
                                name="equipmentTypesMode"
                                checked={equipmentTypesMode === 'all'}
                                onChange={() => {
                                  setEquipmentTypesMode('all');
                                  setDraftEquipmentTypes(null);
                                }}
                              />
                              <span>All equipment types</span>
                            </label>
                            <label className="radio-label">
                              <input
                                type="radio"
                                name="equipmentTypesMode"
                                checked={equipmentTypesMode === 'specific'}
                                onChange={() => {
                                  setEquipmentTypesMode('specific');
                                  setDraftEquipmentTypes([]);
                                }}
                              />
                              <span>Specific equipment types</span>
                            </label>
                          </div>
                          {equipmentTypesMode === 'specific' && (
                            <div className="targeting-checkboxes">
                              {equipmentTypeOptions.filter(t => t.active !== false || draftEquipmentTypes?.includes(t.value)).map(type => (
                                <label key={type.value} className="checkbox-label">
                                  <input
                                    type="checkbox"
                                    checked={draftEquipmentTypes?.includes(type.value) || false}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setDraftEquipmentTypes(prev => [...(prev || []), type.value]);
                                      } else {
                                        setDraftEquipmentTypes(prev => (prev || []).filter(v => v !== type.value));
                                      }
                                    }}
                                  />
                                  <span>{type.label}</span>
                                </label>
                              ))}
                              {(!draftEquipmentTypes || draftEquipmentTypes.length === 0) && (
                                <span className="targeting-warning">No equipment types selected. "All equipment types" will be used on save.</span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {draftUsage === 'scheduled' && (
                      <div className="targeting-row">
                        <div className="form-group">
                          <label>Maintenance Visit Types</label>
                          <span className="targeting-hint">Which visits use this form. Servicing Inspection is the quarterly visit (Quarterly Health Check jobs); Annual Inspection is the annual one (Annual Service jobs).</span>
                          <div className="targeting-mode-selector">
                            <label className="radio-label">
                              <input
                                type="radio"
                                name="maintenanceTypesMode"
                                checked={maintenanceTypesMode === 'all'}
                                onChange={() => {
                                  setMaintenanceTypesMode('all');
                                  setDraftMaintenanceTypes(null);
                                }}
                              />
                              <span>All visit types</span>
                            </label>
                            <label className="radio-label">
                              <input
                                type="radio"
                                name="maintenanceTypesMode"
                                checked={maintenanceTypesMode === 'specific'}
                                onChange={() => {
                                  setMaintenanceTypesMode('specific');
                                  setDraftMaintenanceTypes([]);
                                }}
                              />
                              <span>Specific visit types</span>
                            </label>
                          </div>
                          {maintenanceTypesMode === 'specific' && (
                            <div className="targeting-checkboxes">
                              {MAINTENANCE_VISIT_TYPES.map(type => (
                                <label key={type.value} className="checkbox-label">
                                  <input
                                    type="checkbox"
                                    checked={draftMaintenanceTypes?.includes(type.value) || false}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setDraftMaintenanceTypes(prev => [...(prev || []), type.value]);
                                      } else {
                                        setDraftMaintenanceTypes(prev => (prev || []).filter(v => v !== type.value));
                                      }
                                    }}
                                  />
                                  <span>{type.label}</span>
                                </label>
                              ))}
                              {(!draftMaintenanceTypes || draftMaintenanceTypes.length === 0) && (
                                <span className="targeting-warning">No visit types selected. "All visit types" will be used on save.</span>
                              )}
                            </div>
                          )}

                          {serviceYearsTargetingApplies(maintenanceTypesMode, draftMaintenanceTypes) && (
                            <div className="service-years-targeting">
                              <label>Service Years (annual inspections)</label>
                              <span className="targeting-hint">Target a different annual inspection form to each year of the contract. Annual Service jobs have no contract year, so they only use a form set to All years.</span>
                              <div className="targeting-mode-selector">
                                <label className="radio-label">
                                  <input
                                    type="radio"
                                    name="serviceYearsMode"
                                    checked={serviceYearsMode === 'all'}
                                    onChange={() => {
                                      setServiceYearsMode('all');
                                      setDraftServiceYears(null);
                                    }}
                                  />
                                  <span>All years</span>
                                </label>
                                <label className="radio-label">
                                  <input
                                    type="radio"
                                    name="serviceYearsMode"
                                    checked={serviceYearsMode === 'specific'}
                                    onChange={() => {
                                      setServiceYearsMode('specific');
                                      setDraftServiceYears([]);
                                    }}
                                  />
                                  <span>Specific years</span>
                                </label>
                              </div>
                              {serviceYearsMode === 'specific' && (
                                <div className="targeting-checkboxes">
                                  {SERVICE_YEARS.map(year => (
                                    <label key={year} className="checkbox-label">
                                      <input
                                        type="checkbox"
                                        checked={draftServiceYears?.includes(year) || false}
                                        onChange={(e) => {
                                          if (e.target.checked) {
                                            setDraftServiceYears(prev => [...(prev || []), year].sort((a, b) => a - b));
                                          } else {
                                            setDraftServiceYears(prev => (prev || []).filter(v => v !== year));
                                          }
                                        }}
                                      />
                                      <span>Year {year}</span>
                                    </label>
                                  ))}
                                  {(!draftServiceYears || draftServiceYears.length === 0) && (
                                    <span className="targeting-warning">No years selected. "All years" will be used on save.</span>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    </>
                    )}

                    {draftCheckCategory === 'sales_form' && (
                      Array.isArray(draftTemplate.target_pipeline_stages) && draftTemplate.target_pipeline_stages.length > 0 ? (
                        <div className="targeting-row">
                          <div className="form-group">
                            <label>Pipeline Stages</label>
                            <span className="targeting-hint">
                              This form is triggered by pipeline stages, fixed at creation:{' '}
                              {draftTemplate.target_pipeline_stages
                                .map(pair => `${(pair.product_type || '').replace(/_/g, ' ')} · ${pair.stage_name}`)
                                .join(', ')}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="targeting-row">
                          <div className="form-group">
                            <label>Activity Types</label>
                            <span className="targeting-hint">This form appears automatically when logging an activity of one of these types. Select at least one.</span>
                            <div className="targeting-checkboxes">
                              {getSalesContextCheckboxOptions(draftSalesContexts).map(ctx => (
                                <label key={ctx.value} className="checkbox-label">
                                  <input
                                    type="checkbox"
                                    checked={draftSalesContexts?.includes(ctx.value) || false}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setDraftSalesContexts(prev => [...(prev || []), ctx.value]);
                                      } else {
                                        setDraftSalesContexts(prev => (prev || []).filter(v => v !== ctx.value));
                                      }
                                    }}
                                  />
                                  <span>{ctx.label}</span>
                                </label>
                              ))}
                            </div>
                            {(!draftSalesContexts || draftSalesContexts.length === 0) && (
                              <span className="targeting-warning">No contexts selected — this form will not be offered anywhere until at least one is ticked.</span>
                            )}
                          </div>
                        </div>
                      )
                    )}
                  </div>

                  <div className="draft-sections">
                    <div className="draft-sections-header">
                      <h4>Sections &amp; Questions</h4>
                    </div>
                    <p className="section-hint">
                      Pick a section or question in the outline to change it, and try the form on the right as an engineer would.
                      {' '}Keys are made from the wording and never change, because filled-in forms use them. Anything this form has never published can be deleted; anything published can only be archived.
                    </p>
                    <FormDesigner
                      schema={draftSchema}
                      onChange={setDraftSchema}
                      publishedKeys={draftPublishedKeys}
                    />
                  </div>
                </div>
              )}

              {detailView === 'preview' && (
                <div className="detail-preview">
                  <CheckFormPreview schema={{ ...draftSchema, name: draftName }} />
                </div>
              )}

              <div className="draft-footer">
                <div className="draft-footer-left">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={handleExitDraftEditor}
                    disabled={saving}
                  >
                    ← Template
                  </button>
                </div>
                <div className="draft-footer-right">
                  <button
                    type="button"
                    className="btn-danger"
                    onClick={() => setShowDiscardConfirm(true)}
                    disabled={saving}
                  >
                    Discard Draft
                  </button>
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={handleSaveDraft}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save Draft'}
                  </button>
                  <button
                    type="button"
                    className="btn-primary btn-publish"
                    onClick={() => setShowPublishConfirm(true)}
                    disabled={saving || publishing}
                  >
                    {publishing ? 'Publishing...' : 'Publish Draft'}
                  </button>
                </div>
              </div>

              {publishErrors.length > 0 && (
                <div className="publish-errors">
                  <div className="publish-errors-header">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/>
                      <line x1="15" y1="9" x2="9" y2="15"/>
                      <line x1="9" y1="9" x2="15" y2="15"/>
                    </svg>
                    <span>This template cannot be published until the following issues are resolved:</span>
                  </div>
                  <ul className="publish-errors-list">
                    {publishErrors.map((err, idx) => (
                      <li key={idx}>
                        <strong>{err.sectionTitle ? `${err.sectionTitle}` : ''}{err.fieldLabel ? ` → ${err.fieldLabel}` : ''}{err.sectionTitle || err.fieldLabel ? ': ' : ''}</strong>
                        {err.message}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {targetingConflict && (
                <div className="targeting-conflict-error">
                  <div className="targeting-conflict-header">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                      <line x1="12" y1="9" x2="12" y2="13"/>
                      <line x1="12" y1="17" x2="12.01" y2="17"/>
                    </svg>
                    <span>Targeting Conflict</span>
                  </div>
                  <p className="targeting-conflict-message">
                    This template's targeting overlaps with another active template. Adjust the targeting settings above or archive the conflicting template.
                  </p>
                  <div className="targeting-conflict-templates">
                    <strong>Conflicting template{targetingConflict.conflicting_templates?.length > 1 ? 's' : ''}:</strong>
                    <ul>
                      {targetingConflict.conflicting_templates?.map((t, idx) => (
                        <li key={idx}>
                          <span className="conflict-template-name">{t.name}</span>
                          <span className="conflict-template-meta">
                            ({t.form_type} v{t.version})
                          </span>
                          <div className="conflict-template-targeting">
                            <span>Work logs: {t.target_work_log_types === null ? 'All' : t.target_work_log_types?.map(v => getWorkLogTypeLabel(v, workLogTypeOptions)).join(', ')}</span>
                            <span>Assets: {t.target_asset_types === null ? 'All' : t.target_asset_types?.map(getAssetTypeLabel).join(', ')}</span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

            </div>
          )}

          {/* Read-only Active Template View */}
          {!isEditingDraft && (
            <>
              {!selectedTemplate && !detailLoading && !detailError && (
                <div className="detail-placeholder">
                  <p>Select a template to view its fields and configuration.</p>
                </div>
              )}

              {detailLoading && (
                <div className="detail-loading">
                  <p>Loading template details...</p>
                </div>
              )}

              {detailError && (
                <div className="detail-error">
                  <p>{detailError}</p>
                </div>
              )}

              {existingDraftInfo && (
                <div className="existing-draft-notice">
                  <p>{existingDraftInfo.message}</p>
                  <div className="existing-draft-actions">
                    <button
                      type="button"
                      className="btn-primary"
                      onClick={handleOpenExistingDraft}
                    >
                      Open Existing Draft
                    </button>
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => setExistingDraftInfo(null)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {selectedTemplate && !detailLoading && !detailError && !existingDraftInfo && (
                <div className="template-detail">
                  <div className="detail-header">
                    <div>
                      <h3>{selectedTemplate.name}</h3>
                      <div className="detail-header-meta">
                        <span className={`status-badge ${selectedTemplate.status}`}>
                          {STATUS_LABELS[selectedTemplate.status]}
                        </span>
                        <span className="version-badge">v{selectedTemplate.version}</span>
                        {selectedTemplate.is_default && <span className="default-badge">Default</span>}
                      </div>
                    </div>
                    <div className="detail-header-actions">
                      {canEdit && selectedTemplate.status === 'active' && !draftsMap[selectedTemplate.template_key] && (
                        <button
                          type="button"
                          className="btn-primary"
                          onClick={handleCreateDraft}
                          disabled={detailLoading}
                          title="Create a new version of this template"
                        >
                          Create Draft
                        </button>
                      )}
                      {getOpenableDraftId(selectedTemplate) && (
                        <button
                          type="button"
                          className="btn-primary"
                          onClick={() => handleOpenDraft(getOpenableDraftId(selectedTemplate))}
                          disabled={detailLoading}
                        >
                          Open Draft
                        </button>
                      )}
                      {canEdit && (selectedTemplate.status === 'active' || selectedTemplate.status === 'superseded') && (
                        <button
                          type="button"
                          className="btn-secondary"
                          onClick={openVariantModal}
                          disabled={detailLoading}
                          title="Create a new separate template based on this one"
                        >
                          Create Variant
                        </button>
                      )}
                      {canEdit && (selectedTemplate.status === 'active' || selectedTemplate.status === 'superseded') && (
                        <button
                          type="button"
                          className="btn-secondary"
                          onClick={openRenameModal}
                          disabled={detailLoading || renaming}
                          title="Rename this template"
                        >
                          Rename
                        </button>
                      )}
                      {canEdit && (selectedTemplate.status === 'active' || selectedTemplate.status === 'superseded') && (
                        <button
                          type="button"
                          className="btn-secondary btn-archive"
                          onClick={openArchiveConfirm}
                          disabled={detailLoading || archiving}
                          title="Archive this template"
                        >
                          Archive
                        </button>
                      )}
                      {canEdit && selectedTemplate.status === 'archived' && (
                        <button
                          type="button"
                          className="btn-primary"
                          onClick={handleRestoreTemplate}
                          disabled={detailLoading || restoring}
                          title="Restore this template to active"
                        >
                          {restoring ? 'Restoring...' : 'Restore'}
                        </button>
                      )}
                    </div>
                  </div>

                  {selectedTemplate.status === 'active' && getOpenableDraftId(selectedTemplate) && (
                    <div className="draft-available-notice">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                      </svg>
                      <div className="draft-available-content">
                        <span className="draft-available-title">Draft available</span>
                        <span className="draft-available-desc">This template has unpublished changes in a draft. Click "Open Draft" to continue editing.</span>
                      </div>
                    </div>
                  )}

                  <div className="detail-info">
                    <div className="info-row">
                      <span className="info-label">Form Type</span>
                      <span className="info-value">{formTypeLabel(selectedTemplate.form_type)}</span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">Total Fields</span>
                      <span className="info-value">{countFields(selectedTemplate.schema_json, { activeOnly: true })}</span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">Created</span>
                      <span className="info-value">{formatDate(selectedTemplate.created_at)}</span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">Published</span>
                      <span className="info-value">{formatDate(selectedTemplate.published_at)}</span>
                    </div>
                    {selectedTemplate.schema_json?.description && (
                      <div className="info-row full-width">
                        <span className="info-label">Description</span>
                        <span className="info-value">{selectedTemplate.schema_json.description}</span>
                      </div>
                    )}
                  </div>

                  <div className="detail-targeting">
                    <h4>Targeting</h4>
                    <div className="targeting-info-grid">
                      <div className="targeting-info-row">
                        <span className="targeting-info-label">Category</span>
                        <span className="targeting-info-value">
                          {getCategoryLabel(selectedTemplate.check_category)}
                        </span>
                      </div>
                      <div className="targeting-info-row">
                        <span className="targeting-info-label">Work Log Types</span>
                        <span className="targeting-info-value">
                          {selectedTemplate.target_work_log_types === null
                            ? 'All work log types'
                            : selectedTemplate.target_work_log_types?.map(v => getWorkLogTypeLabel(v, workLogTypeOptions)).join(', ') || '-'}
                        </span>
                      </div>
                      <div className="targeting-info-row">
                        <span className="targeting-info-label">Asset Types</span>
                        <span className="targeting-info-value">
                          {selectedTemplate.target_asset_types === null
                            ? 'All asset types / Not asset-specific'
                            : selectedTemplate.target_asset_types?.map(getAssetTypeLabel).join(', ') || '-'}
                        </span>
                      </div>
                      {Array.isArray(selectedTemplate.target_sales_contexts) && selectedTemplate.target_sales_contexts.length > 0 && (
                        <div className="targeting-info-row">
                          <span className="targeting-info-label">Activity Types</span>
                          <span className="targeting-info-value">
                            {selectedTemplate.target_sales_contexts.map(getSalesContextLabel).join(', ')}
                          </span>
                        </div>
                      )}
                      {Array.isArray(selectedTemplate.target_equipment_types) && selectedTemplate.target_equipment_types.length > 0 && (
                        <div className="targeting-info-row">
                          <span className="targeting-info-label">Equipment Types</span>
                          <span className="targeting-info-value">
                            {selectedTemplate.target_equipment_types.map(v => getEquipmentTypeLabel(v, equipmentTypeOptions)).join(', ')}
                          </span>
                        </div>
                      )}
                      {Array.isArray(selectedTemplate.target_pipeline_stages) && selectedTemplate.target_pipeline_stages.length > 0 && (
                        <div className="targeting-info-row">
                          <span className="targeting-info-label">Pipeline Stages</span>
                          <span className="targeting-info-value">
                            {selectedTemplate.target_pipeline_stages
                              .map(pair => `${(pair.product_type || '').replace(/_/g, ' ')} · ${pair.stage_name}`)
                              .join(', ')}
                            {selectedTemplate.include_in_contract_pack ? ' — joins the Contract Pack' : ''}
                          </span>
                        </div>
                      )}
                    </div>
                    {getTargetingNote(selectedTemplate) && (
                      <p className="targeting-readonly-note">
                        {getTargetingNote(selectedTemplate)}
                      </p>
                    )}
                  </div>

                  <details className="technical-details">
                    <summary>Technical Details</summary>
                    <div className="technical-details-content">
                      <div className="info-row">
                        <span className="info-label">Template ID</span>
                        <span className="info-value monospace">{selectedTemplate.template_id}</span>
                      </div>
                      <div className="info-row">
                        <span className="info-label">Lineage Key</span>
                        <span className="info-value monospace">{selectedTemplate.template_key}</span>
                      </div>
                    </div>
                  </details>

                  <details
                    key={`history-${selectedTemplate.template_id}`}
                    className="technical-details form-history"
                    onToggle={(e) => {
                      if (e.currentTarget.open && formHistory?.templateId !== selectedTemplate.template_id) {
                        loadFormHistory(selectedTemplate.template_id);
                      }
                    }}
                  >
                    <summary>History</summary>
                    <div className="technical-details-content">
                      {formHistory?.templateId === selectedTemplate.template_id && formHistory.loading && (
                        <p className="form-history-note" role="status">Loading history...</p>
                      )}
                      {formHistory?.templateId === selectedTemplate.template_id && formHistory.failed && (
                        <p className="form-history-note">The history could not be loaded. Close and open History to try again.</p>
                      )}
                      {formHistory?.templateId === selectedTemplate.template_id && !formHistory.loading && !formHistory.failed && (
                        formHistory.rows.length === 0 ? (
                          <p className="form-history-note">Nothing recorded yet. Changes are recorded from 21 October 2026.</p>
                        ) : (
                          <ul className="form-history-list">
                            {formHistory.rows.map(row => (
                              <li key={row.audit_id || `${row.changed_at}-${row.change_summary}`}>
                                <span className="form-history-when">{formatDateTime(row.changed_at)}</span>
                                <span className="form-history-who">{row.changed_by_name || 'Unknown'}</span>
                                <span className="form-history-what">{row.change_summary}</span>
                              </li>
                            ))}
                          </ul>
                        )
                      )}
                    </div>
                  </details>

                  <div className="detail-view-tabs">
                    <button
                      type="button"
                      className={`view-tab ${detailView === 'fields' ? 'active' : ''}`}
                      onClick={() => setDetailView('fields')}
                    >
                      Field Details
                    </button>
                    <button
                      type="button"
                      className={`view-tab ${detailView === 'preview' ? 'active' : ''}`}
                      onClick={() => setDetailView('preview')}
                    >
                      Form Preview
                    </button>
                  </div>

                  {detailView === 'fields' && (
                    <div className="detail-sections">
                      <h4>Fields &amp; Questions</h4>
                      {archivedCountOf(selectedTemplate.schema_json) > 0 && (
                        <label className="toggle-label show-archived-toggle">
                          <input
                            type="checkbox"
                            checked={showArchivedInView}
                            onChange={(e) => setShowArchivedInView(e.target.checked)}
                          />
                          <span>
                            Show archived ({archivedCountOf(selectedTemplate.schema_json)}) - not on the live form
                          </span>
                        </label>
                      )}
                      {selectedTemplate.schema_json?.sections
                        ?.filter(section => showArchivedInView || !section.archived)
                        .map((section) => {
                          const shownFields = (section.fields || []).filter(field => showArchivedInView || !field.archived);
                          const liveCount = section.archived ? 0 : (section.fields || []).filter(field => !field.archived).length;
                          return (
                        <div key={section.section_key} className={`check-section ${section.archived ? 'archived' : ''}`}>
                          <div className="section-header">
                            <span className="section-title">
                              {section.title}
                              {section.archived && <span className="archived-badge">Archived</span>}
                            </span>
                            <span className="section-count">{liveCount} field{liveCount !== 1 ? 's' : ''}</span>
                          </div>
                          <div className="section-fields">
                            {shownFields.map((field) => (
                              <React.Fragment key={field.field_key}>
                                {renderFieldDetails(field)}
                                {field.follow_up?.field && (
                                  <div className="follow-up-wrapper">
                                    <span className="follow-up-condition">
                                      When answer is "{field.follow_up.condition}":
                                    </span>
                                    {renderFieldDetails(field.follow_up.field, true)}
                                  </div>
                                )}
                              </React.Fragment>
                            ))}
                          </div>
                        </div>
                          );
                        })}
                    </div>
                  )}

                  {detailView === 'preview' && (
                    <div className="detail-preview">
                      <CheckFormPreview schema={selectedTemplate.schema_json} />
                    </div>
                  )}

                  {saveMessage && (
                    <div className={`save-message ${saveMessage.type}`}>
                      {saveMessage.text}
                    </div>
                  )}

                  <div className="detail-footer">
                    <p className="read-only-notice">
                      {getDetailFooterHint(selectedTemplate)}
                    </p>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Discard Confirmation Modal */}
      {showDiscardConfirm && (
        <div className="modal-backdrop" onClick={() => setShowDiscardConfirm(false)}>
          <div className="modal confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Discard Draft?</h2>
              <button type="button" className="modal-close" onClick={() => setShowDiscardConfirm(false)}>
                &times;
              </button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to discard this draft? All changes will be lost.</p>
              <p className="confirm-detail">The active template will remain unchanged.</p>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setShowDiscardConfirm(false)}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-danger"
                onClick={handleDiscardDraft}
                disabled={saving}
              >
                {saving ? 'Discarding...' : 'Discard Draft'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Publish Confirmation Modal */}
      {showPublishConfirm && (
        <div className="modal-backdrop" onClick={() => setShowPublishConfirm(false)}>
          <div className="modal confirm-modal publish-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Publish Draft?</h2>
              <button type="button" className="modal-close" onClick={() => setShowPublishConfirm(false)}>
                &times;
              </button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to publish this draft template?</p>
              <div className="publish-confirm-details">
                <div className="publish-confirm-item">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  <span>Any changes not yet saved on this page are saved first, then published.</span>
                </div>
                <div className="publish-confirm-item">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  <span>This will make <strong>"{draftName}"</strong> the active template in Settings.</span>
                </div>
                <div className="publish-confirm-item info">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="12" y1="16" x2="12" y2="12"/>
                    <line x1="12" y1="8" x2="12.01" y2="8"/>
                  </svg>
                  <span>New checks will use this version wherever its targeting matches, including in Log Work.</span>
                </div>
                <div className="publish-confirm-item">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                  </svg>
                  <span>Existing completed checks will not be changed.</span>
                </div>
              </div>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setShowPublishConfirm(false)}
                disabled={publishing}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={handlePublishDraft}
                disabled={publishing}
              >
                {publishing ? 'Publishing...' : 'Publish Template'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create New Template Modal */}
      {showCreateNewModal && (
        <div className="modal-backdrop" onClick={closeCreateNewModal}>
          <div className="modal create-new-template-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create New Template</h2>
              <button type="button" className="modal-close" onClick={closeCreateNewModal}>
                &times;
              </button>
            </div>
            <div className="modal-body">
              <p className="modal-intro">
                Create a new check form template from scratch. You can add sections and fields after creation.
              </p>

              {createNewError && (
                <div className="modal-error">
                  {createNewError}
                </div>
              )}

              <div className="form-group">
                <label>Template Name <span className="required">*</span></label>
                <input
                  type="text"
                  value={newTemplateData.name}
                  onChange={(e) => setNewTemplateData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g. BCI Parlour Check"
                  autoFocus
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <input
                  type="text"
                  value={newTemplateData.description}
                  onChange={(e) => setNewTemplateData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Optional description"
                />
              </div>

              <div className="form-group">
                <label>Check Category</label>
                <select
                  value={newTemplateData.check_category}
                  onChange={(e) => {
                    const value = e.target.value;
                    setNewTemplateData(prev => ({ ...prev, check_category: value }));
                    // Load the sales-context options lazily, only when the manager
                    // actually picks Sales Form - keeps the create flow's fetch
                    // sequence unchanged for every other category.
                    if (value === 'sales_form' && salesContextOptions.length === 0) {
                      loadSalesContexts();
                    }
                    if (value === 'equipment_check' && equipmentTypeOptions.length === 0) {
                      loadEquipmentTypes();
                    }
                  }}
                >
                  {CHECK_CATEGORIES.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
                <span className="form-help">
                  {CHECK_CATEGORIES.find(c => c.value === newTemplateData.check_category)?.description}
                </span>
              </div>

              {newTemplateData.check_category === 'equipment_check' && (
                <div className="form-group">
                  <label>Equipment Types</label>
                  <span className="targeting-hint">Which registered equipment (vans, toolboxes, hired kit) presents this form.</span>
                  <div className="targeting-mode-toggle">
                    <label className="radio-label">
                      <input
                        type="radio"
                        checked={newTemplateData.equipmentTypesMode === 'all'}
                        onChange={() => setNewTemplateData(prev => ({ ...prev, equipmentTypesMode: 'all', targetEquipmentTypes: [] }))}
                      />
                      <span>All equipment types</span>
                    </label>
                    <label className="radio-label">
                      <input
                        type="radio"
                        checked={newTemplateData.equipmentTypesMode === 'specific'}
                        onChange={() => setNewTemplateData(prev => ({ ...prev, equipmentTypesMode: 'specific' }))}
                      />
                      <span>Specific equipment types</span>
                    </label>
                  </div>
                  {newTemplateData.equipmentTypesMode === 'specific' && (
                    <div className="targeting-checkboxes">
                      {equipmentTypeOptions.filter(t => t.active !== false).map(type => (
                        <label key={type.value} className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={newTemplateData.targetEquipmentTypes.includes(type.value)}
                            onChange={(e) => {
                              setNewTemplateData(prev => ({
                                ...prev,
                                targetEquipmentTypes: e.target.checked
                                  ? [...prev.targetEquipmentTypes, type.value]
                                  : prev.targetEquipmentTypes.filter(v => v !== type.value)
                              }));
                            }}
                          />
                          <span>{type.label}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {newTemplateData.check_category === 'sales_form' && (
                <div className="form-group">
                  <label>Used For</label>
                  <span className="targeting-hint">An activity form is either attached to logged activities (contexts) OR triggered when a line item enters a pipeline stage — never both.</span>
                  <div className="targeting-mode-toggle">
                    <label className="radio-label">
                      <input
                        type="radio"
                        checked={newTemplateData.salesTargetMode === 'contexts'}
                        onChange={() => setNewTemplateData(prev => ({ ...prev, salesTargetMode: 'contexts' }))}
                      />
                      <span>Logged activity types</span>
                    </label>
                    <label className="radio-label">
                      <input
                        type="radio"
                        checked={newTemplateData.salesTargetMode === 'stages'}
                        onChange={() => {
                          setNewTemplateData(prev => ({ ...prev, salesTargetMode: 'stages' }));
                          if (Object.keys(pipelineStagesByType).length === 0) loadPipelineStages();
                        }}
                      />
                      <span>Pipeline stage trigger</span>
                    </label>
                  </div>
                </div>
              )}

              {newTemplateData.check_category === 'sales_form' && newTemplateData.salesTargetMode === 'contexts' && (
                <div className="form-group">
                  <label>Activity Types *</label>
                  <span className="targeting-hint">This form appears automatically when logging an activity of one of these types. Select at least one.</span>
                  <div className="targeting-checkboxes">
                    {salesContextOptions.filter(ctx => ctx.active !== false).map(ctx => (
                      <label key={ctx.value} className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={newTemplateData.targetSalesContexts.includes(ctx.value)}
                          onChange={(e) => {
                            setNewTemplateData(prev => ({
                              ...prev,
                              targetSalesContexts: e.target.checked
                                ? [...prev.targetSalesContexts, ctx.value]
                                : prev.targetSalesContexts.filter(v => v !== ctx.value)
                            }));
                          }}
                        />
                        <span>{ctx.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {newTemplateData.check_category === 'sales_form' && newTemplateData.salesTargetMode === 'stages' && (
                <>
                  <div className="form-group">
                    <label>Pipeline Stages *</label>
                    <span className="targeting-hint">
                      Entering a targeted stage demands a scheduled task; the form is completed at that task's sign-off. Select a product type, then tick its stages.
                    </span>
                    <select
                      value={stageTargetProduct}
                      onChange={(e) => setStageTargetProduct(e.target.value)}
                    >
                      <option value="">Select product type...</option>
                      {Object.keys(pipelineStagesByType).sort().map(type => (
                        <option key={type} value={type}>{type.replace(/_/g, ' ')}</option>
                      ))}
                    </select>
                    {stageTargetProduct && (
                      <div className="targeting-checkboxes">
                        {(pipelineStagesByType[stageTargetProduct] || []).map(stage => {
                          const checked = newTemplateData.targetPipelineStages.some(
                            pair => pair.product_type === stageTargetProduct && pair.stage_name === stage.stage_name
                          );
                          return (
                            <label key={stage.stage_name} className="checkbox-label">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(e) => {
                                  setNewTemplateData(prev => ({
                                    ...prev,
                                    targetPipelineStages: e.target.checked
                                      ? [...prev.targetPipelineStages, { product_type: stageTargetProduct, stage_name: stage.stage_name }]
                                      : prev.targetPipelineStages.filter(
                                        pair => !(pair.product_type === stageTargetProduct && pair.stage_name === stage.stage_name)
                                      )
                                  }));
                                }}
                              />
                              <span>{stage.stage_name}</span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                    {newTemplateData.targetPipelineStages.length > 0 && (
                      <span className="form-help">
                        Targeted: {newTemplateData.targetPipelineStages.map(pair => `${pair.product_type.replace(/_/g, ' ')} · ${pair.stage_name}`).join(', ')}
                      </span>
                    )}
                  </div>
                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={newTemplateData.includeInContractPack}
                        onChange={(e) => setNewTemplateData(prev => ({ ...prev, includeInContractPack: e.target.checked }))}
                      />
                      <span>Include in Contract Pack (site survey report — completed submissions render to PDF and join the pack)</span>
                    </label>
                  </div>
                </>
              )}

              {newTemplateData.check_category !== 'sales_form' && (
              <div className="form-group">
                <label>Used For</label>
                <span className="targeting-hint">Where this form is used. A form is for one or the other, never both.</span>
                <div className="targeting-mode-toggle">
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="createNewUsage"
                      checked={newTemplateData.usage === 'reactive'}
                      onChange={() => setNewTemplateData(prev => ({ ...prev, usage: 'reactive' }))}
                    />
                    <span>Reactive work: tickets and all other jobs</span>
                  </label>
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="createNewUsage"
                      checked={newTemplateData.usage === 'scheduled'}
                      onChange={() => setNewTemplateData(prev => ({ ...prev, usage: 'scheduled' }))}
                    />
                    <span>Scheduled maintenance: Log Work on Quarterly Health Check and Annual Service jobs, and maintenance sign-off</span>
                  </label>
                </div>
              </div>
              )}

              {newTemplateData.check_category !== 'sales_form' && newTemplateData.usage === 'reactive' && (
              <div className="form-group">
                <label>Target Work Log Types</label>
                <div className="targeting-mode-toggle">
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="createNewWorkLogTypesMode"
                      checked={newTemplateData.workLogTypesMode === 'all'}
                      onChange={() => setNewTemplateData(prev => ({ ...prev, workLogTypesMode: 'all', targetWorkLogTypes: [] }))}
                    />
                    <span>All work types</span>
                  </label>
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="createNewWorkLogTypesMode"
                      checked={newTemplateData.workLogTypesMode === 'specific'}
                      onChange={() => setNewTemplateData(prev => ({ ...prev, workLogTypesMode: 'specific' }))}
                    />
                    <span>Specific types</span>
                  </label>
                </div>
                {newTemplateData.workLogTypesMode === 'specific' && (
                  <div className="targeting-checkboxes">
                    {getWorkLogTypeCheckboxOptions(newTemplateData.targetWorkLogTypes).map(type => (
                      <label key={type.value} className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={newTemplateData.targetWorkLogTypes.includes(type.value)}
                          onChange={(e) => {
                            setNewTemplateData(prev => ({
                              ...prev,
                              targetWorkLogTypes: e.target.checked
                                ? [...prev.targetWorkLogTypes, type.value]
                                : prev.targetWorkLogTypes.filter(v => v !== type.value)
                            }));
                          }}
                        />
                        <span>{type.label}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              )}

              <div className="form-group">
                <label>Target Asset Types</label>
                <div className="targeting-mode-toggle">
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="createNewAssetTypesMode"
                      checked={newTemplateData.assetTypesMode === 'all'}
                      onChange={() => setNewTemplateData(prev => ({ ...prev, assetTypesMode: 'all', targetAssetTypes: [] }))}
                    />
                    <span>All assets / Not asset-specific</span>
                  </label>
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="createNewAssetTypesMode"
                      checked={newTemplateData.assetTypesMode === 'specific'}
                      onChange={() => setNewTemplateData(prev => ({ ...prev, assetTypesMode: 'specific' }))}
                    />
                    <span>Specific asset types</span>
                  </label>
                </div>
                {newTemplateData.assetTypesMode === 'specific' && (
                  <div className="targeting-checkboxes grouped">
                    {assetTypeGroups.map(group => (
                      <div key={group.label} className="asset-group">
                        <span className="group-label">{group.label}</span>
                        <div className="group-types">
                          {group.types.map(type => (
                            <label key={type.value} className="checkbox-label">
                              <input
                                type="checkbox"
                                checked={newTemplateData.targetAssetTypes.includes(type.value)}
                                onChange={(e) => {
                                  setNewTemplateData(prev => ({
                                    ...prev,
                                    targetAssetTypes: e.target.checked
                                      ? [...prev.targetAssetTypes, type.value]
                                      : prev.targetAssetTypes.filter(v => v !== type.value)
                                  }));
                                }}
                              />
                              <span>{type.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {newTemplateData.check_category !== 'sales_form' && newTemplateData.usage === 'scheduled' && (
                <div className="form-group">
                  <label>Maintenance Visit Types</label>
                  <span className="targeting-hint">Which visits use this form. Servicing Inspection is the quarterly visit (Quarterly Health Check jobs); Annual Inspection is the annual one (Annual Service jobs).</span>
                  <div className="targeting-mode-toggle">
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="createNewMaintenanceTypesMode"
                        checked={newTemplateData.maintenanceTypesMode === 'all'}
                        onChange={() => setNewTemplateData(prev => ({ ...prev, maintenanceTypesMode: 'all', targetMaintenanceTypes: [] }))}
                      />
                      <span>All visit types</span>
                    </label>
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="createNewMaintenanceTypesMode"
                        checked={newTemplateData.maintenanceTypesMode === 'specific'}
                        onChange={() => setNewTemplateData(prev => ({ ...prev, maintenanceTypesMode: 'specific' }))}
                      />
                      <span>Specific visit types</span>
                    </label>
                  </div>
                  {newTemplateData.maintenanceTypesMode === 'specific' && (
                    <div className="targeting-checkboxes">
                      {MAINTENANCE_VISIT_TYPES.map(type => (
                        <label key={type.value} className="checkbox-label" title={type.description}>
                          <input
                            type="checkbox"
                            checked={newTemplateData.targetMaintenanceTypes.includes(type.value)}
                            onChange={(e) => {
                              setNewTemplateData(prev => ({
                                ...prev,
                                targetMaintenanceTypes: e.target.checked
                                  ? [...prev.targetMaintenanceTypes, type.value]
                                  : prev.targetMaintenanceTypes.filter(v => v !== type.value)
                              }));
                            }}
                          />
                          <span>{type.label}</span>
                        </label>
                      ))}
                    </div>
                  )}

                  {serviceYearsTargetingApplies(newTemplateData.maintenanceTypesMode, newTemplateData.targetMaintenanceTypes) && (
                    <div className="service-years-targeting">
                      <label>Service Years (annual inspections)</label>
                      <span className="targeting-hint">Target a different annual inspection form to each year of the contract. Annual Service jobs have no contract year, so they only use a form set to All years.</span>
                      <div className="targeting-mode-toggle">
                        <label className="radio-label">
                          <input
                            type="radio"
                            name="createNewServiceYearsMode"
                            checked={newTemplateData.serviceYearsMode === 'all'}
                            onChange={() => setNewTemplateData(prev => ({ ...prev, serviceYearsMode: 'all', targetServiceYears: [] }))}
                          />
                          <span>All years</span>
                        </label>
                        <label className="radio-label">
                          <input
                            type="radio"
                            name="createNewServiceYearsMode"
                            checked={newTemplateData.serviceYearsMode === 'specific'}
                            onChange={() => setNewTemplateData(prev => ({ ...prev, serviceYearsMode: 'specific' }))}
                          />
                          <span>Specific years</span>
                        </label>
                      </div>
                      {newTemplateData.serviceYearsMode === 'specific' && (
                        <div className="targeting-checkboxes">
                          {SERVICE_YEARS.map(year => (
                            <label key={year} className="checkbox-label">
                              <input
                                type="checkbox"
                                checked={newTemplateData.targetServiceYears.includes(year)}
                                onChange={(e) => {
                                  setNewTemplateData(prev => ({
                                    ...prev,
                                    targetServiceYears: e.target.checked
                                      ? [...prev.targetServiceYears, year].sort((a, b) => a - b)
                                      : prev.targetServiceYears.filter(v => v !== year)
                                  }));
                                }}
                              />
                              <span>Year {year}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={closeCreateNewModal}
                disabled={createNewLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={handleCreateNewTemplate}
                disabled={createNewLoading || !newTemplateData.name.trim()}
              >
                {createNewLoading ? 'Creating...' : 'Create Template'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Archive Confirmation Modal */}
      {showArchiveConfirm && (
        <div className="modal-backdrop" onClick={() => setShowArchiveConfirm(false)}>
          <div className="modal confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Archive Template?</h2>
              <button type="button" className="modal-close" onClick={() => setShowArchiveConfirm(false)}>
                &times;
              </button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to archive <strong>"{selectedTemplate?.name}"</strong>?</p>
              {archiveImpact?.loading && (
                <p className="archive-impact-note" role="status">Checking what this form is used for...</p>
              )}
              {archiveImpact && !archiveImpact.loading && archiveImpact.uncovered.length > 0 && (
                <div className="archive-impact-warning" role="alert">
                  <strong>Engineers will get no form at all for:</strong>
                  <ul>
                    {archiveImpact.uncovered.slice(0, 12).map(label => <li key={label}>{label}</li>)}
                  </ul>
                  {archiveImpact.uncovered.length > 12 && (
                    <p>and {archiveImpact.uncovered.length - 12} more.</p>
                  )}
                  <p>Publish a replacement first if this work should still have a form.</p>
                </div>
              )}
              {archiveImpact && !archiveImpact.loading && !archiveImpact.failed && archiveImpact.uncovered.length === 0 && (
                <p className="archive-impact-note">Everything this form is used for has another form to fall back on.</p>
              )}
              {archiveImpact?.failed && (
                <p className="archive-impact-note">Could not check what this form is used for. Check Show Coverage after archiving.</p>
              )}
              <div className="archive-confirm-details">
                <div className="archive-confirm-item">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="12" y1="16" x2="12" y2="12"/>
                    <line x1="12" y1="8" x2="12.01" y2="8"/>
                  </svg>
                  <span>Archived templates will no longer be available in Log Work for new check entries.</span>
                </div>
                <div className="archive-confirm-item">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                  </svg>
                  <span>Existing completed checks will not be affected.</span>
                </div>
                <div className="archive-confirm-item">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="9 14 4 9 9 4"/>
                    <path d="M20 20v-7a4 4 0 0 0-4-4H4"/>
                  </svg>
                  <span>You can restore archived templates later if needed.</span>
                </div>
              </div>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setShowArchiveConfirm(false)}
                disabled={archiving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-danger"
                onClick={handleArchiveTemplate}
                disabled={archiving}
              >
                {archiving ? 'Archiving...' : 'Archive Template'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Restore Conflict Notice */}
      {restoreConflict && (
        <div className="modal-backdrop" onClick={() => setRestoreConflict(null)}>
          <div className="modal confirm-modal restore-conflict-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Cannot Restore Template</h2>
              <button type="button" className="modal-close" onClick={() => setRestoreConflict(null)}>
                &times;
              </button>
            </div>
            <div className="modal-body">
              <p>This template cannot be restored because its targeting conflicts with another active template.</p>
              <div className="conflict-details">
                <p><strong>Conflicting templates:</strong></p>
                <ul className="conflict-list">
                  {restoreConflict.conflicting_templates?.map((conflict, idx) => (
                    <li key={idx}>
                      <strong>{conflict.name}</strong> (v{conflict.version})
                      <span className="conflict-reason"> - {conflict.reason || 'Overlapping targeting'}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <p className="conflict-hint">
                To restore this template, first archive or modify the conflicting template's targeting.
              </p>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setRestoreConflict(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rename Modal */}
      {showVariantModal && selectedTemplate && (
        <div className="modal-backdrop" onClick={closeVariantModal}>
          <div className="modal rename-modal variant-modal" role="dialog" aria-label="Create Variant" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create Variant</h2>
              <button type="button" className="modal-close" onClick={closeVariantModal}>
                &times;
              </button>
            </div>
            <div className="modal-body">
              {variantForm.error && (
                <div className="modal-error">
                  {variantForm.error}
                </div>
              )}
              <p className="rename-note">
                A new form based on "{selectedTemplate.name}". Its questions are copied; anything archived on
                "{selectedTemplate.name}" is left behind. Engineers see this name and description at the top of the form.
              </p>
              <div className="form-group">
                <label htmlFor="variant-name">Name <span className="required">*</span></label>
                <input
                  id="variant-name"
                  type="text"
                  value={variantForm.name}
                  onChange={(e) => setVariantForm(prev => ({ ...prev, name: e.target.value, error: '' }))}
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label htmlFor="variant-description">Description</label>
                <input
                  id="variant-description"
                  type="text"
                  value={variantForm.description}
                  onChange={(e) => setVariantForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="What this form is for"
                />
              </div>
              <p className="rename-note">
                The variant starts with the same targeting as "{selectedTemplate.name}". Change it in the draft before
                publishing, and check the wording of the completion tick at the end of the form.
              </p>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={closeVariantModal}
                disabled={detailLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={handleCreateVariantDraft}
                disabled={detailLoading || !variantForm.name.trim()}
              >
                {detailLoading ? 'Creating...' : 'Create Variant'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showRenameModal && (
        <div className="modal-backdrop" onClick={closeRenameModal}>
          <div className="modal rename-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Rename Template</h2>
              <button type="button" className="modal-close" onClick={closeRenameModal}>
                &times;
              </button>
            </div>
            <div className="modal-body">
              {renameError && (
                <div className="modal-error">
                  {renameError}
                </div>
              )}
              <div className="form-group">
                <label>Template Name <span className="required">*</span></label>
                <input
                  type="text"
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  placeholder="Enter new template name"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !renaming) {
                      handleRenameTemplate();
                    }
                  }}
                />
              </div>
              <p className="rename-note">
                Renaming the template will update its display name. Existing completed checks will retain the original template name from when they were created.
              </p>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={closeRenameModal}
                disabled={renaming}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={handleRenameTemplate}
                disabled={renaming || !renameValue.trim()}
              >
                {renaming ? 'Renaming...' : 'Rename'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CheckFormSettings;
